/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CANVAS_ENTITY_EVENT,
  SHAPES,
  TRIPOD_ARROW_MIN_LENGTH,
  TRIPOD_ARROW_MIN_LENGTH_FACTOR,
} from '../../../const'
import { computeTripodArrowDirection } from './tripodEntity'
import type { BaseBalloonExtension } from '../../base/baseBalloonExtension'
import type { TripodShapePoints } from '@/app/data/type'
import { TRIPOD_CONSTANTS } from '../../constants'
import _ from 'lodash'
import { EventManager } from '../../../eventManager'
import type {
  IEllipse,
  IGroup,
  ILine,
  INode,
  IPointerEvent,
  IRenderer,
  ISelectionHandles,
  IShape,
  IText,
} from '../../../backend/IRenderer'
import type { Vector2d } from '../../../backend/types'
import { isEllipseNode, isLineNode } from '../../../backend/nodeGuards'

// TypeScript의 Constructor 타입 정의
type Constructor<T = object> = new (...args: any[]) => T

// Base 클래스에서 필요한 속성/메소드들의 인터페이스
interface SelectionHandlerMethods {
  // 공통 속성들
  TRANSFORMER_HIT_STROKE_WIDTH: number
  entityType: SHAPES
  __shape_wrapper?: IGroup
  __tripod_transform_group?: IGroup
  __shapeTransformer?: ISelectionHandles
  __color?: string
  calculatedPointSize: number
  calculatedConnectedLineWidth: number
  balloon?: any // Balloon 모듈
  isSnapped: boolean

  // 메소드들
  __destroyTransformer(): void
  getEntityInteractionMode(): boolean
  getTripodShapePoints(): TripodShapePoints | null
  setTripodShapePoints(points: TripodShapePoints): void
  getStroke(): string
  setTailStyle(tailStyle: 'arrow' | 'dot'): void
  updateBalloonPosition(): void
  updateRipplePosition(): void
  __hideButtonGroup(): void
  __showButtonGroup(): void
  __updateButtonPosition(): void
  addShapeAnimation(): void
  removeShapeAnimation(): void
  setEntityPosition(x: number, y: number): void
  getId(): string
  isTripod(): boolean
  getRippleFollowMode(): boolean
  canvasEngine: {
    getRenderer(): IRenderer
    initShapeTransformer(transformer: ISelectionHandles): void
    disableAnotherEntityListening(id: string): void
    restoreAllEntitiyListening(): void
    events: {
      onEntityTransform(id: string): void
    }
  }

  // TRIPOD 관련 메소드들 (Mixin에서 추가됨)
  __addTripodHandler?(): void
  __cleanupTripodStageEvents?(): void
  __removeTripodEditButtons?(): void
  __removeTripodElementHandlers?(): void
  __resetTripodStartPointHover?(): void
  __calculateTripodButtonPosition?(): { x: number; y: number }
  __updateTripodColors?(color: string): void
}

/** 믹스인이 클래스 밖(엔티티 사용처)에 여는 멤버. 그 외 멤버는 믹스인 안에서만 쓴다 */
export interface TripodSelectionApi {
  handleTripodTailShape(tripodTailShapeId: string): void
}

/**
 * TRIPOD 선택 및 조작 기능을 제공하는 Mixin
 *
 * 반환 타입을 Base 타입 + TripodSelectionApi로 둔다(any면 상속 체인 아래 엔티티 전체가 any가 돼 tsc가 오용을 못 잡는다).
 * 믹스인 내부 private·protected 멤버는 반환 타입에 노출하지 않는다 — BaseSelectionHandler의 같은 이름 필드
 * (`__animationFrameId`: useDefineForClassFields로 인스턴스 필드가 믹스인 접근자를 가리는 기존 동작)와 타입 충돌을 피한다.
 */
export function TripodSelectionMixin<T extends Constructor<BaseBalloonExtension>>(
  Base: T,
): T & Constructor<TripodSelectionApi> {
  return class TripodSelection extends Base {
    // Base 클래스의 메소드들에 타입 안전하게 접근하기 위한 헬퍼
    private get selectionHandler(): SelectionHandlerMethods {
      return this as unknown as SelectionHandlerMethods
    }

    // TRIPOD 상태 관리 중앙화
    private __tripodState = {
      // UI 요소들
      editButtonGroup: undefined as IGroup | undefined,
      plusButtons: [] as IGroup[],
      minusButtons: [] as IGroup[],
      moveButton: null as IGroup | null,
      // 캐시된 시각적 요소들
      cachedMainAxisLine: null as ILine | null,
      cachedConnectionLines: [] as ILine[],
      cachedBalloonConnectedLine: null as ILine | null,
      cachedBalloonText: null as IText | null,

      // 애니메이션 및 이벤트
      animationFrameId: null as number | null,
      isUpdating: false,

      // 회전 관련
      rotationCenter: null as { x: number; y: number } | null,

      // Transform 중복 호출 방지
      transformCompleted: false,
    }

    // EventManager 인스턴스 (Base 클래스에서 상속받지만 타입 안전성을 위해 명시)
    protected eventManager = EventManager.getInstance()

    // 레거시 호환성을 위한 getter들 (외부에서 접근하는 경우 대비)
    protected get __tripodEditButtonGroup(): IGroup | undefined {
      return this.__tripodState.editButtonGroup
    }
    protected set __tripodEditButtonGroup(value: IGroup | undefined) {
      this.__tripodState.editButtonGroup = value
    }

    protected get __tripodPlusButtons(): IGroup[] {
      return this.__tripodState.plusButtons
    }
    protected get __tripodMinusButtons(): IGroup[] {
      return this.__tripodState.minusButtons
    }
    /**
     * TRIPOD shape 핸들러
     * 회전만 가능하고 크기 변경은 불가능한 transformer + 개별 요소 드래그 기능
     * balloon도 transformer 그룹에 포함되어 함께 변형됨
     */
    protected __addTripodHandler(): void {
      const systemLayer = this.renderer.layers.system
      if (!systemLayer) {
        throw new Error('시스템 레이어가 없습니다.')
      }

      if (!this.__shape_wrapper) {
        throw new Error('TRIPOD 도형이 없습니다.')
      }

      // TRIPOD 선택 시 startpoint hover 상태 리셋
      this.__resetTripodStartPointHover()

      const tripodShapePoints = this.selectionHandler.getTripodShapePoints()
      if (!tripodShapePoints) {
        throw new Error('TRIPOD shape points가 없습니다.')
      }

      this.selectionHandler.__destroyTransformer()

      // transformer 대상 그룹 사용 (balloon 포함)
      const transformTarget = this.selectionHandler.__tripod_transform_group
      if (!transformTarget) {
        throw new Error('TRIPOD transformer 대상 그룹이 없습니다.')
      }

      const interactionEnabled = this.selectionHandler.getEntityInteractionMode()
      // 전체 그룹 드래그 가능하게 설정 (전체 wrapper 그룹 사용)
      const target = this.selectionHandler.__shape_wrapper
      target?.draggable(
        interactionEnabled && (this as any).isSelected && !this.canvasEngine.isPinchZooming,
      )

      // transformer 설정 (회전만 가능, 크기 변경 불가능)
      // balloon 포함 모든 요소들이 transformer 영역으로 설정
      this.selectionHandler.__shapeTransformer = this.createSelectionHandles(
        {
          id: 'single-selection',
          padding: 20,
          anchorCornerRadius: 20,
          anchorSize: 20, // 다른 shape들과 동일한 앵커 크기
          rotateEnabled: interactionEnabled, // 회전만 활성화
          resizeEnabled: false, // 크기 변경 비활성화
          enabledAnchors: [], // 리사이즈 앵커는 모두 비활성화 (회전 핸들만 표시)
          borderStrokeWidth: 2,
          ignoreStroke: true,
          borderHitStrokeWidth: TRIPOD_CONSTANTS.TRANSFORMER_HIT_STROKE_WIDTH,
          draggable: interactionEnabled,
          anchorDraggable: false, // 앵커 드래그 비활성화 (회전 핸들은 동작)
        },
        [transformTarget],
      )

      // 모든 TRIPOD 이벤트 핸들러를 중앙에서 설정 (중복 방지)
      this.__setupAllTripodEvents()

      // TRIPOD 편집 버튼들 추가 (+, - 버튼)
      this.__addTripodEditButtons()

      this.canvasEngine.initShapeTransformer(this.selectionHandler.__shapeTransformer!)
      systemLayer.add(this.selectionHandler.__shapeTransformer!.node)
      systemLayer.moveToTop()
    }

    /**
     * TRIPOD 개별 요소들의 드래그 핸들러 추가 (최적화된 DOM 탐색)
     */
    private __addTripodElementHandlers(): void {
      const tripodTransformGroup = this.selectionHandler.__tripod_transform_group
      if (!tripodTransformGroup) {
        return
      }

      const tripodShapePoints = this.selectionHandler.getTripodShapePoints()
      if (!tripodShapePoints) {
        return
      }

      // DOM 탐색을 한 번만 실행하고 모든 필요한 요소들을 찾기
      const startPointElements: IEllipse[] = []
      let mainAxisElement: ILine | null = null
      const connectionLineElements: ILine[] = []

      tripodTransformGroup.getChildren().forEach(node => {
        if (isEllipseNode(node) && node.name() === 'startPoint') {
          startPointElements.push(node)
        } else if (isLineNode(node) && node.name() === 'mainAxis') {
          mainAxisElement = node
        } else if (isLineNode(node) && node.name() === 'connectionLine') {
          connectionLineElements.push(node)
        }
      })

      // 시작점들에 드래그 핸들러 추가
      startPointElements.forEach((startPoint, index) => {
        if (index < tripodShapePoints.dots.length) {
          this.__addStartPointHandler(startPoint, index)
        }
      })

      // 주축선에 드래그 핸들러 추가
      if (mainAxisElement) {
        this.__addMainAxisHandler(mainAxisElement)
      }

      // 연결선은 드래그 없음 — 끝점 이동은 끝점(점/화살표) 드래그, 전체 이동은 이동 버튼이 전담
    }

    /**
     * TRIPOD startpoint의 hover 상태를 리셋
     */
    private __resetTripodStartPointHover(): void {
      if (this.entityType !== SHAPES.TRIPOD || !this.__tripod_transform_group) return
      // 마우스 커서도 기본으로 복원
      this.renderer.stage.container().style.cursor = 'default'
    }

    /**
     * TRIPOD 개별 요소들의 드래그 핸들러 제거 (최적화된 DOM 탐색)
     */
    private __removeTripodElementHandlers(): void {
      if (!this.__tripod_transform_group) {
        return
      }

      // DOM 탐색을 한 번만 실행하고 모든 대상 요소들을 찾기
      const targetElements: Array<{
        node: INode
        type: 'startPoint' | 'mainAxis' | 'connectionLine'
      }> = []

      this.__tripod_transform_group.getChildren().forEach(node => {
        if (isEllipseNode(node) && node.name() === 'startPoint') {
          targetElements.push({ node, type: 'startPoint' })
        } else if (isLineNode(node) && node.name() === 'mainAxis') {
          targetElements.push({ node, type: 'mainAxis' })
        } else if (isLineNode(node) && node.name() === 'connectionLine') {
          targetElements.push({ node, type: 'connectionLine' })
        }
      })

      // 모든 대상 요소들의 이벤트 일괄 정리
      targetElements.forEach(({ node, type }) => {
        this.__removeNodeEvents(node, type)
      })
    }

    /**
     * 개별 노드의 이벤트들을 안전하게 제거하는 헬퍼 메서드
     */
    private __removeNodeEvents(
      node: INode,
      type: 'startPoint' | 'mainAxis' | 'connectionLine' | 'editButton',
    ): void {
      // EventManager 이벤트들 제거 (안전하게)
      let eventTypes: CANVAS_ENTITY_EVENT[]

      if (type === 'startPoint') {
        eventTypes = [
          CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_ENTER,
          CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_LEAVE,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_START,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_END,
        ]
      } else if (type === 'mainAxis') {
        eventTypes = [
          CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_ENTER,
          CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_LEAVE,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_START,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_END,
        ]
      } else if (type === 'connectionLine') {
        eventTypes = [
          CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_ENTER,
          CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_LEAVE,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_START,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE,
          CANVAS_ENTITY_EVENT.SELECT_DRAG_END,
        ]
      } else if (type === 'editButton') {
        eventTypes = [
          CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_ENTER,
          CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_LEAVE,
          CANVAS_ENTITY_EVENT.EDIT_BUTTON_CLICK,
          CANVAS_ENTITY_EVENT.EDIT_BUTTON_TAP,
        ]
      } else {
        eventTypes = []
      }

      eventTypes.forEach(eventType => {
        try {
          this.eventManager.removeEvent(node, eventType)
        } catch {
          // 이벤트가 등록되지 않았을 수도 있으므로 에러 무시
        }
      })

      // draggable 비활성화 (편집 버튼은 제외)
      if (type !== 'editButton') {
        node.draggable(false)
      }
    }

    /**
     * TRIPOD Transformer 이벤트 핸들러 추가 (마우스 좌표 기반 각도 계산)
     */
    private __addTripodTransformerHandlers(): void {
      if (!this.__shapeTransformer) return
      if (!this.__shape_wrapper) return
      if (!this.__tripod_transform_group) return

      const targetWrapper = this.__shape_wrapper // 전체 그룹 (드래그 대상)
      const transformTarget = this.__tripod_transform_group // transformer 대상 그룹

      // TRIPOD 회전용 변수 (마우스 좌표 기반)
      let rotationOrigin: { x: number; y: number } | null = null
      let fixedRotationCenter: { x: number; y: number } | null = null
      let lastMouseAngle = 0 // 마우스 각도 (0~359도)
      let isRotating = false

      // 변형 시작 시 (이벤트 중복 방지는 __setupAllTripodEvents에서 처리됨)
      this.eventManager.addEvent(
        transformTarget,
        CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_START,
        () => {
          if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
          this.selectionHandler.removeShapeAnimation()
          this.selectionHandler.__hideButtonGroup()
          this.__hideTripodEditButtons()

          // Transform 중복 호출 방지 플래그 초기화
          this.__tripodState.transformCompleted = false

          // TRIPOD 회전 처리용 초기화
          rotationOrigin = transformTarget.position()
          // 회전 시작 시 회전 중심을 한번만 계산하고 고정
          fixedRotationCenter = this.__calculateTripodRotationCenter()
          // 상태에도 저장 (중앙화된 관리)
          this.__tripodState.rotationCenter = fixedRotationCenter

          // 마우스 시작 각도 계산
          const mousePos = this.__getStageRelativeMousePosition()
          if (mousePos && fixedRotationCenter) {
            lastMouseAngle = this.__calculateMouseAngle(mousePos, fixedRotationCenter)
            isRotating = true
          }

          // 최초 생성 TRIPOD의 경우 캐시 강제 초기화 (balloon text 분리 방지)
          this.__invalidateVisualElementsCache()
          this.__cacheVisualElements()
        },
      )

      // 실시간 transform 처리 (마우스 좌표 기반 각도 계산)
      this.__shapeTransformer.off('transform')
      this.__shapeTransformer.on('transform', () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        if (rotationOrigin && fixedRotationCenter && isRotating) {
          // Transformer가 핸들 위치를 변경하지 못하도록 고정
          transformTarget.position(rotationOrigin)
          transformTarget.rotation(0) // Transformer 회전값은 항상 0으로 유지

          // 현재 마우스 위치에서 각도 계산
          const mousePos = this.__getStageRelativeMousePosition()

          if (mousePos) {
            const currentMouseAngle = this.__calculateMouseAngle(mousePos, fixedRotationCenter)

            // 각도 차이 계산 (0도/359도 순환 고려)
            const deltaAngle = this.__calculateAngleDelta(lastMouseAngle, currentMouseAngle)

            if (Math.abs(deltaAngle) > 0.05) {
              // 0.05도 이상 변화 시에만 적용 (더욱 민감하게 변경)
              // 실제 TRIPOD 좌표 업데이트 (고정된 회전축 사용)
              this.__rotateTripodByMouseAngle(deltaAngle, fixedRotationCenter)
              lastMouseAngle = currentMouseAngle
            }
          }

          // 회전 중에는 편집 버튼들 확실히 숨김 유지
          this.__hideTripodEditButtons()
        }
      })

      // 변형 종료 시 (이벤트 중복 방지는 __setupAllTripodEvents에서 처리됨)
      this.eventManager.addEvent(transformTarget, CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_END, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        // TRIPOD 회전 완료 처리
        if (rotationOrigin) {
          transformTarget.position(rotationOrigin)
        }
        transformTarget.rotation(0) // Transformer rotation 항상 0으로 유지

        // 회전 상태 초기화
        isRotating = false
        lastMouseAngle = 0
        rotationOrigin = null
        fixedRotationCenter = null

        // 회전 완료 후 transformer 경계 업데이트 (성능상 회전 중에는 생략했음)
        this.__updateTripodTransformer()

        this.selectionHandler.addShapeAnimation()
        this.updateBalloonPosition()
        this.selectionHandler.__showButtonGroup()
        this.selectionHandler.__updateButtonPosition()
        this.__showTripodEditButtons()
        this.__updateTripodEditButtonsPosition()

        // TRIPOD 그룹의 변형된 위치를 Entity 위치로 반영 (드래그 처리)
        const wrapperX = targetWrapper.x()
        const wrapperY = targetWrapper.y()

        if (wrapperX !== 0 || wrapperY !== 0) {
          // TRIPOD 그룹 위치를 원점으로 리셋
          targetWrapper.x(0)
          targetWrapper.y(0)

          // Entity 위치를 기존 위치 + 이동 거리로 업데이트
          this.setEntityPosition(this.node.x() + wrapperX, this.node.y() + wrapperY)

          // TRIPOD 내부 데이터도 이동 거리만큼 업데이트
          this.__updateTripodPositionData(wrapperX, wrapperY)
        }

        // balloon 위치 업데이트
        this.updateBalloonPosition()

        // 변형 완료 이벤트 호출 (중복 방지 - 한 번만 호출)
        if (!this.__tripodState.transformCompleted) {
          this.__tripodState.transformCompleted = true
          this.canvasEngine.events.onEntityTransform(this.getId())
        }

        // TRIPOD 편집 버튼들 위치 업데이트 및 다시 표시
        this.__updateTripodEditButtonsPosition()
        this.__showTripodEditButtons()
        // transformer 완전히 재생성
        this.__recreateTripodTransformer()

        // 변형 종료 후 복사버튼 그룹 다시 보이기 및 위치 업데이트
        this.selectionHandler.__showButtonGroup()
        this.selectionHandler.__updateButtonPosition()
        this.selectionHandler.addShapeAnimation()
        this.__showPlusMinusButtons()
        this.__updateTripodTransformer()
        this.__updateTripodVisualElements() // recreate 대신 update 사용

        this.canvasEngine.restoreAllEntitiyListening()
      })

      // 객체가 변형될 때 벌룬의 위치를 업데이트 (이벤트 중복 방지는 __setupAllTripodEvents에서 처리됨)
      this.eventManager.addEvent(transformTarget, CANVAS_ENTITY_EVENT.SELECT_TRANSFORMED, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        // TRIPOD 편집 버튼들 위치 업데이트 및 다시 표시
        this.canvasEngine.disableAnotherEntityListening(this.getId())
        this.__updateTripodEditButtonsPosition()
        this.__showTripodEditButtons()

        // 변형 종료 후 복사버튼 그룹 다시 보이기 및 위치 업데이트
        this.selectionHandler.__showButtonGroup()
        this.selectionHandler.__updateButtonPosition()
      })

      // TRIPOD 드래그 시작 시 파동 애니메이션 처리 (이벤트 중복 방지는 __setupAllTripodEvents에서 처리됨)
      this.eventManager.addEvent(targetWrapper, CANVAS_ENTITY_EVENT.SELECT_DRAG_START, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return

        // Transform 중복 호출 방지 플래그 초기화 (드래그만 발생하는 경우 대비)
        if (!this.__tripodState.transformCompleted) {
          this.__tripodState.transformCompleted = false
        }

        // 파동 애니메이션 모드에 따른 처리
        if (this.balloon) {
          if (!this.getRippleFollowMode()) {
            this.selectionHandler.removeShapeAnimation()
          }
        }
        this.selectionHandler.__hideButtonGroup()
        // TRIPOD 편집 버튼들 숨기기
        this.__hideTripodEditButtons()
      })

      // TRIPOD 드래그 중 복사버튼 그룹 숨기기 (이벤트 중복 방지는 __setupAllTripodEvents에서 처리됨)
      this.eventManager.addEvent(targetWrapper, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        // 파동 애니메이션 따라다니기 모드일 때 파동 위치 업데이트
        if (this.balloon) {
          if (this.getRippleFollowMode()) {
            this.selectionHandler.updateRipplePosition()
          }
        }

        // 스냅된 상태가 아닐 때만 위치 업데이트
        if (!this.snapManager.getIsSnapped()) {
          this.updateBalloonPosition()
        }
        this.selectionHandler.__hideButtonGroup()
      })

      // TRIPOD 드래그 종료 시 변경사항 저장 (이벤트 중복 방지는 __setupAllTripodEvents에서 처리됨)
      this.eventManager.addEvent(targetWrapper, CANVAS_ENTITY_EVENT.SELECT_DRAG_END, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        // TRIPOD 그룹의 드래그된 위치를 Entity 위치로 반영 (다른 shape들과 동일한 로직)
        const wrapperX = targetWrapper.x()
        const wrapperY = targetWrapper.y()

        if (wrapperX !== 0 || wrapperY !== 0) {
          // TRIPOD 그룹 위치를 원점으로 리셋
          targetWrapper.x(0)
          targetWrapper.y(0)

          // Entity 위치를 기존 위치 + 이동 거리로 업데이트
          this.setEntityPosition(this.node.x() + wrapperX, this.node.y() + wrapperY)

          // TRIPOD 내부 데이터도 이동 거리만큼 업데이트
          this.__updateTripodPositionData(wrapperX, wrapperY)
        }

        // balloon 위치 업데이트
        this.updateBalloonPosition()

        // 파동 애니메이션 모드에 따른 처리
        if (this.balloon) {
          if (!this.getRippleFollowMode()) {
            this.selectionHandler.addShapeAnimation()
          } else {
            this.selectionHandler.updateRipplePosition()
          }
        }

        // 변형 완료 이벤트 호출 (중복 방지 - 한 번만 호출)
        if (!this.__tripodState.transformCompleted) {
          this.__tripodState.transformCompleted = true
          this.canvasEngine.events.onEntityTransform(this.getId())
        }

        // TRIPOD 편집 버튼들 위치 업데이트 및 다시 표시
        this.__updateTripodEditButtonsPosition()
        this.__showTripodEditButtons()

        // 드래그 종료 후 복사버튼 그룹 다시 보이기 및 위치 업데이트
        this.selectionHandler.__showButtonGroup()
        this.selectionHandler.__updateButtonPosition()
      })
    }

    /**
     * TRIPOD 전체 이동 시 위치 데이터 업데이트
     */
    private __updateTripodPositionData(deltaX: number, deltaY: number): void {
      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // 주축선 포인트들 업데이트 (절대좌표)
      tripodShapePoints.mainAxisPoint1[0] += deltaX
      tripodShapePoints.mainAxisPoint1[1] += deltaY
      tripodShapePoints.mainAxisPoint2[0] += deltaX
      tripodShapePoints.mainAxisPoint2[1] += deltaY

      // 시작점들 업데이트 (절대좌표)
      tripodShapePoints.dots.forEach((dot: number[]) => {
        dot[0] += deltaX
        dot[1] += deltaY
      })

      // 업데이트된 데이터 저장
      this.setTripodShapePoints(tripodShapePoints)
    }

    protected __hidePlusMinusButtons(): void {
      if (this.__tripodPlusButtons.length > 0) {
        this.__tripodPlusButtons.forEach(button => {
          button.visible(false)
        })
      }
      if (this.__tripodMinusButtons.length > 0) {
        this.__tripodMinusButtons.forEach(button => {
          button.visible(false)
        })
      }
    }

    protected __showPlusMinusButtons(): void {
      if (this.__tripodPlusButtons.length > 0) {
        this.__tripodPlusButtons.forEach(button => {
          button.visible(true)
        })
      }
      if (this.__tripodMinusButtons.length > 0) {
        this.__tripodMinusButtons.forEach(button => {
          button.visible(true)
        })
      }
    }
    /**
     * TRIPOD 회전 중심축을 계산 (회전 시작 시 한번만 계산)
     */
    private __calculateTripodRotationCenter(): { x: number; y: number } {
      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return { x: 0, y: 0 }

      // 주축선의 중심점 계산
      const mainAxisCenter = {
        x: (tripodShapePoints.mainAxisPoint1[0] + tripodShapePoints.mainAxisPoint2[0]) / 2,
        y: (tripodShapePoints.mainAxisPoint1[1] + tripodShapePoints.mainAxisPoint2[1]) / 2,
      }

      // 모든 startPoint들의 중심점 계산
      const startPointsCenter = {
        x:
          tripodShapePoints.dots.reduce((sum, dot) => sum + dot[0], 0) /
          tripodShapePoints.dots.length,
        y:
          tripodShapePoints.dots.reduce((sum, dot) => sum + dot[1], 0) /
          tripodShapePoints.dots.length,
      }

      // 각 startPoint에서 주축선까지의 수직 거리 계산하여 최대값 찾기
      let maxDistance = 0
      for (const dot of tripodShapePoints.dots) {
        const startPoint = { x: dot[0], y: dot[1] }
        const projectedPoint = this.__projectPointOntoMainAxis(startPoint, tripodShapePoints)
        const distance = Math.sqrt(
          Math.pow(startPoint.x - projectedPoint.x, 2) +
            Math.pow(startPoint.y - projectedPoint.y, 2),
        )
        maxDistance = Math.max(maxDistance, distance)
      }

      // 주축선 중심에서 startPoint 방향으로 최대 거리만큼 떨어진 지점을 회전 중심으로 사용
      const directionToStartPoints = {
        x: startPointsCenter.x - mainAxisCenter.x,
        y: startPointsCenter.y - mainAxisCenter.y,
      }

      const directionLength = Math.sqrt(
        directionToStartPoints.x * directionToStartPoints.x +
          directionToStartPoints.y * directionToStartPoints.y,
      )

      return directionLength === 0 || maxDistance === 0
        ? startPointsCenter
        : {
            x: mainAxisCenter.x + (directionToStartPoints.x / directionLength) * maxDistance,
            y: mainAxisCenter.y + (directionToStartPoints.y / directionLength) * maxDistance,
          }
    }

    /**
     * Stage의 scale과 position을 고려한 정확한 마우스 좌표 획득
     * @returns Stage 상대 좌표계의 마우스 위치
     */
    private __getStageRelativeMousePosition(): { x: number; y: number } | null {
      const stage = this.renderer.stage
      const pointerPos = this.renderer.getPointerPosition()

      if (!pointerPos) return null

      // Stage의 transform 정보 가져오기
      const stageX = stage.x()
      const stageY = stage.y()
      const scaleX = stage.scaleX()
      const scaleY = stage.scaleY()

      // Stage transform을 역으로 적용하여 실제 캔버스 좌표 계산
      const relativeX = (pointerPos.x - stageX) / scaleX
      const relativeY = (pointerPos.y - stageY) / scaleY

      return { x: relativeX, y: relativeY }
    }
    /**
     * 중심축 기준 마우스 각도 계산 (수직 위쪽이 0도, 시계방향으로 증가)
     * @param mousePos 마우스 좌표
     * @param rotationCenter 회전 중심점
     * @returns 0~359도 사이의 각도
     */
    private __calculateMouseAngle(
      mousePos: { x: number; y: number },
      rotationCenter: { x: number; y: number },
    ): number {
      // 마우스와 회전 중심 간의 벡터
      const deltaX = mousePos.x - rotationCenter.x
      const deltaY = mousePos.y - rotationCenter.y

      // atan2를 사용해서 라디안 각도 계산 (-π ~ π)
      // atan2(y, x)는 x축 기준 각도를 반환하므로, 수직 위쪽을 0도로 만들기 위해 π/2를 빼줌
      const angleRad = Math.atan2(deltaX, -deltaY) // y를 음수로 해서 위쪽이 0도가 되도록

      // 라디안을 도로 변환
      let angleDeg = (angleRad * 180) / Math.PI

      // 음수 각도를 0~359도 범위로 변환
      if (angleDeg < 0) {
        angleDeg += 360
      }

      return angleDeg
    }

    /**
     * 0도/359도 순환을 고려한 각도 차이 계산
     * @param fromAngle 시작 각도 (0~359도)
     * @param toAngle 목표 각도 (0~359도)
     * @returns -180~180도 사이의 각도 차이 (양수: 시계방향, 음수: 반시계방향)
     */
    private __calculateAngleDelta(fromAngle: number, toAngle: number): number {
      let delta = toAngle - fromAngle

      // 180도를 넘는 차이는 반대 방향으로 회전하는 것이 더 가까움
      if (delta > 180) {
        delta -= 360
      } else if (delta < -180) {
        delta += 360
      }

      return delta
    }

    /**
     * 마우스 각도 기반 TRIPOD 회전 처리 (startPoint 완전 고정, 주축선만 회전)
     * @param deltaAngle 회전각 차이 (degree, 양수: 시계방향, 음수: 반시계방향)
     * @param rotationCenter 고정된 회전 중심점
     */
    private __rotateTripodByMouseAngle(
      deltaAngle: number,
      rotationCenter: { x: number; y: number },
    ): void {
      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // degree를 radian으로 변환
      const angleRad = (deltaAngle * Math.PI) / 180
      const cos = Math.cos(angleRad)
      const sin = Math.sin(angleRad)

      // 점 회전 함수 (고정된 회전 중심축 사용)
      const rotatePoint = (p: { x: number; y: number }): { x: number; y: number } => {
        const dx = p.x - rotationCenter.x
        const dy = p.y - rotationCenter.y
        return {
          x: dx * cos - dy * sin + rotationCenter.x,
          y: dx * sin + dy * cos + rotationCenter.y,
        }
      }

      // 주축선만 회전 (startPoint(dots)는 절대 건드리지 않음!)
      // 중요: tripodShapePoints.dots 배열은 절대 수정하지 않음
      const rotatedMainAxisStart = rotatePoint({
        x: tripodShapePoints.mainAxisPoint1[0],
        y: tripodShapePoints.mainAxisPoint1[1],
      })
      const rotatedMainAxisEnd = rotatePoint({
        x: tripodShapePoints.mainAxisPoint2[0],
        y: tripodShapePoints.mainAxisPoint2[1],
      })

      tripodShapePoints.mainAxisPoint1[0] = rotatedMainAxisStart.x
      tripodShapePoints.mainAxisPoint1[1] = rotatedMainAxisStart.y
      tripodShapePoints.mainAxisPoint2[0] = rotatedMainAxisEnd.x
      tripodShapePoints.mainAxisPoint2[1] = rotatedMainAxisEnd.y

      // balloon 회전 (기존과 동일한 방식)
      if (this.balloon) {
        const entityX = this.node.x()
        const entityY = this.node.y()

        const balloonPos = this.balloon.getPosition()
        const currentBalloonPos = {
          x: entityX + balloonPos.x,
          y: entityY + balloonPos.y,
        }

        const rotatedBalloonPos = rotatePoint(currentBalloonPos)
        this.balloon.setPosition({
          x: rotatedBalloonPos.x - entityX,
          y: rotatedBalloonPos.y - entityY,
        })
      }

      this.__hidePlusMinusButtons()
      // 회전 후 주축선 길이 재조정 (startPoint들과의 관계에 맞춰)
      this.__adjustMainAxisLength(tripodShapePoints)

      // 데이터 업데이트
      this.setTripodShapePoints(tripodShapePoints)

      // 회전 중에는 startPoint 고정, 주축선/연결선/balloon만 실시간 업데이트
      this.__updateTripodDuringRotation()
    }

    // 레거시 호환성을 위한 캐시 변수 getter들
    private get __cachedMainAxisLine(): ILine | null {
      return this.__tripodState.cachedMainAxisLine
    }
    private set __cachedMainAxisLine(value: ILine | null) {
      this.__tripodState.cachedMainAxisLine = value
    }

    private get __cachedConnectionLines(): ILine[] {
      return this.__tripodState.cachedConnectionLines
    }

    private get __cachedBalloonConnectedLine(): ILine | null {
      return this.__tripodState.cachedBalloonConnectedLine
    }
    private set __cachedBalloonConnectedLine(value: ILine | null) {
      this.__tripodState.cachedBalloonConnectedLine = value
    }

    private get __cachedBalloonText(): IText | null {
      return this.__tripodState.cachedBalloonText
    }
    private set __cachedBalloonText(value: IText | null) {
      this.__tripodState.cachedBalloonText = value
    }

    private get __animationFrameId(): number | null {
      return this.__tripodState.animationFrameId
    }
    private set __animationFrameId(value: number | null) {
      this.__tripodState.animationFrameId = value
    }

    /**
     * 모든 TRIPOD 이벤트 핸들러를 중앙에서 정리 (중복 방지)
     */
    private __removeAllTripodEvents(): void {
      // 중복 실행 방지
      if (this.__tripodState.isUpdating) return
      this.__tripodState.isUpdating = true

      try {
        // Transformer 이벤트 정리
        if (this.__tripod_transform_group) {
          this.eventManager.removeEvent(
            this.__tripod_transform_group,
            CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_START,
          )
          this.eventManager.removeEvent(
            this.__tripod_transform_group,
            CANVAS_ENTITY_EVENT.SELECT_TRANSFORM_END,
          )
          this.eventManager.removeEvent(
            this.__tripod_transform_group,
            CANVAS_ENTITY_EVENT.SELECT_TRANSFORMED,
          )
        }

        if (this.__shape_wrapper) {
          this.eventManager.removeEvent(this.__shape_wrapper, CANVAS_ENTITY_EVENT.SELECT_DRAG_START)
          this.eventManager.removeEvent(this.__shape_wrapper, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE)
          this.eventManager.removeEvent(this.__shape_wrapper, CANVAS_ENTITY_EVENT.SELECT_DRAG_END)
        }

        // 개별 요소 이벤트 정리
        this.__removeTripodElementHandlers()

        // Transformer 정리
        if (this.__shapeTransformer) {
          this.__shapeTransformer.off('transform')
        }

        // 캐시 무효화
        this.__invalidateVisualElementsCache()
      } finally {
        this.__tripodState.isUpdating = false
      }
    }

    /**
     * 모든 TRIPOD 이벤트 핸들러를 중앙에서 설정 (중복 방지)
     */
    private __setupAllTripodEvents(): void {
      // 중복 실행 방지
      if (this.__tripodState.isUpdating) return

      // 기존 이벤트 먼저 정리
      this.__removeAllTripodEvents()

      this.__tripodState.isUpdating = true

      try {
        // 1. Transformer 이벤트 설정
        this.__addTripodTransformerHandlers()

        // 2. 개별 요소 이벤트 설정
        this.__addTripodElementHandlers()

        // 3. 캐시 초기화
        this.__cacheVisualElements()
      } finally {
        this.__tripodState.isUpdating = false
      }
    }

    /**
     * 배치 업데이트 메서드 (중복 호출 방지)
     */
    private __batchUpdateTripod(
      options: {
        updateTransformer?: boolean
        updateButtons?: boolean
        updateVisuals?: boolean
        updateBalloon?: boolean
        forceRender?: boolean
      } = {},
    ): void {
      // 중복 실행 방지
      if (this.__tripodState.isUpdating) return

      this.__tripodState.isUpdating = true

      try {
        // 옵션에 따라 필요한 업데이트만 실행
        if (options.updateVisuals !== false) {
          this.__updateTripodVisualElements()
        }

        if (options.updateBalloon !== false) {
          this.updateBalloonPosition()
        }

        if (options.updateTransformer !== false) {
          this.__updateTripodTransformer()
        }

        if (options.updateButtons !== false) {
          this.__updateTripodEditButtonsPosition()
          this.__showTripodEditButtons()
          this.selectionHandler.__showButtonGroup()
          this.selectionHandler.__updateButtonPosition()
        }

        if (options.forceRender) {
          this.__forceImmediateRender()
        } else {
          this.__requestBatchDraw()
        }
      } finally {
        this.__tripodState.isUpdating = false
      }
    }

    /**
     * 회전 중 업데이트 (성능 최적화 버전 - startPoint는 절대 고정, 주축선/연결선만 실시간 업데이트)
     */
    private __updateTripodDuringRotation(): void {
      if (!this.__tripod_transform_group) return

      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      const entityX = this.node.x()
      const entityY = this.node.y()

      // 1. 캐시가 없으면 초기화
      if (!this.__cachedMainAxisLine || this.__cachedConnectionLines.length === 0) {
        this.__cacheVisualElements()
      }

      // 2. 캐시가 여전히 없으면 직접 DOM 탐색 (fallback)
      if (!this.__cachedMainAxisLine) {
        // 직접 주축선 찾기
        this.__tripod_transform_group.getChildren().forEach(node => {
          if (isLineNode(node) && node.name() === 'mainAxis') {
            node.points([
              tripodShapePoints.mainAxisPoint1[0] - entityX,
              tripodShapePoints.mainAxisPoint1[1] - entityY,
              tripodShapePoints.mainAxisPoint2[0] - entityX,
              tripodShapePoints.mainAxisPoint2[1] - entityY,
            ])
          } else if (isLineNode(node) && node.name() === 'connectionLine') {
            // 연결선들도 직접 업데이트
            const points = node.points()
            if (points.length >= 2) {
              const startX = points[0] + entityX
              const startY = points[1] + entityY
              const startPoint = { x: startX, y: startY }
              const connectionEnd = this.__projectPointOntoMainAxis(startPoint, tripodShapePoints)
              node.points([
                startPoint.x - entityX,
                startPoint.y - entityY,
                connectionEnd.x - entityX,
                connectionEnd.y - entityY,
              ])
            }
          }
        })
      } else {
        // 캐시된 주축선 업데이트
        this.__cachedMainAxisLine.points([
          tripodShapePoints.mainAxisPoint1[0] - entityX,
          tripodShapePoints.mainAxisPoint1[1] - entityY,
          tripodShapePoints.mainAxisPoint2[0] - entityX,
          tripodShapePoints.mainAxisPoint2[1] - entityY,
        ])

        // 캐시된 연결선들 실시간 업데이트
        for (
          let i = 0;
          i < tripodShapePoints.dots.length && i < this.__cachedConnectionLines.length;
          i++
        ) {
          const dot = tripodShapePoints.dots[i]
          const connectionLine = this.__cachedConnectionLines[i]

          if (connectionLine) {
            const startPoint = { x: dot[0], y: dot[1] }
            const connectionEnd = this.__projectPointOntoMainAxis(startPoint, tripodShapePoints)

            connectionLine.points([
              startPoint.x - entityX,
              startPoint.y - entityY,
              connectionEnd.x - entityX,
              connectionEnd.y - entityY,
            ])
          }
        }
      }

      // 3. balloon connectedLine 및 text 직접 업데이트 (무거운 updateBalloonPosition 대신)
      if (this.balloon) {
        const balloonPos = this.balloon.getPosition()
        const balloonAbsX = entityX + balloonPos.x
        const balloonAbsY = entityY + balloonPos.y
        const balloonAbsPos = { x: balloonAbsX, y: balloonAbsY }
        const balloonConnectionEnd = this.__projectPointOntoMainAxis(
          balloonAbsPos,
          tripodShapePoints,
        )

        // 캐시된 connectedLine 또는 balloon에서 직접 가져오기
        const connectedLine = this.__cachedBalloonConnectedLine || this.balloon.connectedLine.line
        if (connectedLine) {
          // balloon 경계에서 시작하도록 오프셋 (투명 채움 시 연결선이 벌룬 내부를 통과해 보이지 않도록)
          connectedLine.points(
            this.offsetConnectorStartToBalloonEdge(balloonPos, {
              x: balloonConnectionEnd.x - entityX,
              y: balloonConnectionEnd.y - entityY,
            }),
          )
        }

        // balloon text도 실시간 업데이트
        const balloonText = this.__cachedBalloonText || this.balloon.head.text
        if (balloonText) {
          balloonText.x(balloonPos.x)
          balloonText.y(balloonPos.y)
        }
      }

      // 4. transformer 업데이트는 회전 중에는 생략 (성능 최적화)
      // this.__updateTripodTransformer() // 회전 완료 후에만 실행

      // 5. 즉시 렌더링 (회전 중 실시간 업데이트를 위해)
      this.__forceImmediateRender()
    }

    /**
     * batchDraw 호출을 throttling (성능 최적화)
     */
    private __requestBatchDraw(): void {
      if (this.__animationFrameId !== null) {
        return // 이미 예약된 batchDraw가 있으면 무시
      }

      this.__animationFrameId = requestAnimationFrame(() => {
        this.__animationFrameId = null
        this.node.getLayer()?.batchDraw()
      })
    }

    /**
     * 즉시 렌더링 (회전 중 실시간 업데이트용)
     */
    private __forceImmediateRender(): void {
      if (this.__animationFrameId !== null) {
        cancelAnimationFrame(this.__animationFrameId)
        this.__animationFrameId = null
      }

      // TRIPOD 요소들이 있는 레이어와 시스템 레이어 모두 업데이트
      const layer = this.node.getLayer()
      if (layer) {
        layer.batchDraw()
      }

      // 시스템 레이어도 업데이트 (transformer 등)
      const systemLayer = this.renderer.layers.system
      if (systemLayer) {
        systemLayer.batchDraw()
      }
    }

    /**
     * 시각적 요소들 캐싱 (성능 최적화 - 중앙화된 상태 관리)
     */
    private __cacheVisualElements(): void {
      if (!this.__tripod_transform_group) return

      // 상태 초기화
      this.__tripodState.cachedMainAxisLine = null
      this.__tripodState.cachedConnectionLines = []
      this.__tripodState.cachedBalloonConnectedLine = null

      // DOM 탐색을 한 번만 실행하고 모든 요소를 캐싱
      this.__tripod_transform_group.getChildren().forEach(node => {
        if (isLineNode(node)) {
          if (node.name() === 'mainAxis') {
            this.__tripodState.cachedMainAxisLine = node
          } else if (node.name() === 'connectionLine') {
            this.__tripodState.cachedConnectionLines.push(node)
          }
        }
      })

      // balloon 요소들 캐싱
      if (this.balloon) {
        this.__tripodState.cachedBalloonConnectedLine = this.balloon.connectedLine.line
        this.__tripodState.cachedBalloonText = this.balloon.head.text
      }
    }

    /**
     * 캐시 무효화 (중앙화된 상태 관리 - 요소가 재생성될 때 호출)
     */
    private __invalidateVisualElementsCache(): void {
      // 애니메이션 프레임 취소 (불필요한 렌더링 방지)
      if (this.__tripodState.animationFrameId !== null) {
        cancelAnimationFrame(this.__tripodState.animationFrameId)
        this.__tripodState.animationFrameId = null
      }

      // 모든 캐시 초기화
      this.__tripodState.cachedMainAxisLine = null
      this.__tripodState.cachedConnectionLines = []
      this.__tripodState.cachedBalloonConnectedLine = null
      this.__tripodState.cachedBalloonText = null
      this.__tripodState.rotationCenter = null
      this.__tripodState.transformCompleted = false // Transform 상태도 초기화
    }

    /**
     * 시작점 드래그 핸들러 (EventManager 사용)
     */
    private __addStartPointHandler(hitCircle: IEllipse, index: number): void {
      const interactionEnabled = this.getEntityInteractionMode()

      if (!interactionEnabled) {
        return
      }

      // 같은 위치의 visual element 찾기 (Ellipse 또는 Line)
      const tripodTransformGroup = this.__tripod_transform_group
      let visualElement: IShape | null = null

      if (tripodTransformGroup) {
        tripodTransformGroup.getChildren((node: INode) => {
          if (node.name() === 'startPointVisual') {
            // Ellipse (dot) 또는 Line (triangle) 모두 허용
            if (isEllipseNode(node)) {
              // Ellipse의 경우 위치 비교
              if (
                Math.abs(node.x() - hitCircle.x()) < 1 &&
                Math.abs(node.y() - hitCircle.y()) < 1
              ) {
                visualElement = node
                return true
              }
            } else if (isLineNode(node)) {
              // Line의 경우 중심이 hitCircle 위치와 가까운지 확인
              if (
                Math.abs(node.x() - hitCircle.x()) < 2 &&
                Math.abs(node.y() - hitCircle.y()) < 2
              ) {
                visualElement = node
                return true
              }
            }
          }
          return false
        })
      }

      if (!visualElement) {
        return
      }

      // Konva의 기본 draggable 사용
      hitCircle.draggable(
        interactionEnabled && (this as any).isSelected && !this.canvasEngine.isPinchZooming,
      )

      // 화살표 모드: 수직거리가 임계 미만이면 주축선 위로 스냅되도록 드래그 자체를 제한
      // (드래그 후 위치를 되돌리는 방식은 프레임마다 포인터 위치와 싸우며 버벅임을 유발)
      hitCircle.dragBoundFunc((pos: Vector2d): Vector2d => {
        const minLength = this.__getTripodArrowMinLength()
        const tripodShapePoints = this.getTripodShapePoints()
        const parent = hitCircle.getParent()
        if (minLength <= 0 || !tripodShapePoints || !parent) return pos

        const transform = parent.getAbsoluteTransform()
        const local = transform.copy().invert().point(pos)
        const absX = local.x + this.node.x()
        const absY = local.y + this.node.y()

        const direction = this.__getMainAxisDirection(tripodShapePoints)
        const p1 = tripodShapePoints.mainAxisPoint1
        const relX = absX - p1[0]
        const relY = absY - p1[1]
        const perpDist = relX * -direction.y + relY * direction.x
        if (Math.abs(perpDist) >= minLength) return pos

        const along = relX * direction.x + relY * direction.y
        return transform.point({
          x: p1[0] + direction.x * along - this.node.x(),
          y: p1[1] + direction.y * along - this.node.y(),
        })
      })

      // EventManager를 통한 이벤트 등록
      this.eventManager.removeEvent(hitCircle, CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_ENTER)
      this.eventManager.addEvent(hitCircle, CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_ENTER, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        console.info('ENTITY_FOCUS_MOUSE_ENTER')
        this.renderer.stage.container().style.cursor = 'move'
        // 십자가 이동 커서
        visualElement?.getLayer()?.batchDraw()
      })

      this.eventManager.removeEvent(hitCircle, CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_LEAVE)
      this.eventManager.addEvent(hitCircle, CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_LEAVE, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        console.info('ENTITY_FOCUS_MOUSE_LEAVE')
        this.renderer.stage.container().style.cursor = 'default' // 기본 커서로 복원
        visualElement?.getLayer()?.batchDraw()
      })

      // 드래그 시작
      this.eventManager.removeEvent(hitCircle, CANVAS_ENTITY_EVENT.SELECT_DRAG_START)
      this.eventManager.addEvent(hitCircle, CANVAS_ENTITY_EVENT.SELECT_DRAG_START, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        // 드래그 중 커서 유지
        this.renderer.stage.container().style.cursor = 'move'

        // 파동 애니메이션 제거 및 버튼그룹 숨김
        this.selectionHandler.removeShapeAnimation()
        this.selectionHandler.__hideButtonGroup()
        // TRIPOD 편집 버튼들 숨기기
        this.__hideTripodEditButtons()
      })

      // 드래그 중
      this.eventManager.removeEvent(hitCircle, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE)
      this.eventManager.addEvent(hitCircle, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        const hitX = hitCircle.x()
        const hitY = hitCircle.y()

        // visual element도 같이 이동 (Ellipse 또는 Line)
        if (isEllipseNode(visualElement)) {
          visualElement.x(hitX)
          visualElement.y(hitY)
        } else if (isLineNode(visualElement)) {
          // Line의 경우 위치만 변경 (points는 상대좌표로 유지)
          visualElement.x(hitX)
          visualElement.y(hitY)
        }

        // TRIPOD 구조 업데이트
        this.__updateTripodOnStartPointMove(index, hitX, hitY)
      })

      // 드래그 종료
      this.eventManager.removeEvent(hitCircle, CANVAS_ENTITY_EVENT.SELECT_DRAG_END)
      this.eventManager.addEvent(hitCircle, CANVAS_ENTITY_EVENT.SELECT_DRAG_END, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        // 커서 기본으로 복원
        this.renderer.stage.container().style.cursor = 'default'
        visualElement?.getLayer()?.batchDraw()

        // 변형 완료 처리
        this.canvasEngine.events.onEntityTransform(this.getId())

        // Transformer 경계 영역 업데이트 (startpoint 이동 반영)
        this.__updateTripodTransformer()

        // 파동 애니메이션 및 버튼그룹 복원
        if (this.balloon) {
          if (!this.getRippleFollowMode()) {
            this.selectionHandler.addShapeAnimation()
          } else {
            this.selectionHandler.updateRipplePosition()
          }
        }

        // TRIPOD 편집 버튼들 위치 업데이트 및 다시 표시
        this.__updateTripodEditButtonsPosition()
        this.__showTripodEditButtons()

        // 드래그 종료 후 이벤트 핸들러 재설정 제거 (__updateTripodVisualElements는 기존 요소 재사용하므로 핸들러 유지됨)
        // this.__addTripodElementHandlers()

        this.selectionHandler.__showButtonGroup()
        this.selectionHandler.__updateButtonPosition()
      })
    }

    /**
     * 시작점 이동 시 TRIPOD 업데이트 (EventManager 방식)
     */
    private __updateTripodOnStartPointMove(index: number, newX: number, newY: number): void {
      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // 상대좌표를 절대좌표로 변환
      const entityX = this.node.x()
      const entityY = this.node.y()
      let absoluteX = newX + entityX
      let absoluteY = newY + entityY

      // 화살표 모드: 수직거리가 최소길이 임계 미만이면 주축선 위(수직거리 0)로 스냅
      // 드래그는 dragBoundFunc가 1차 제한하지만, 그 외 경로 방어를 위해 데이터에서도 스냅
      const minLength = this.__getTripodArrowMinLength()
      if (minLength > 0) {
        const direction = this.__getMainAxisDirection(tripodShapePoints)
        const p1 = tripodShapePoints.mainAxisPoint1
        const relX = absoluteX - p1[0]
        const relY = absoluteY - p1[1]
        const perpDist = relX * -direction.y + relY * direction.x
        if (Math.abs(perpDist) < minLength) {
          const along = relX * direction.x + relY * direction.y
          absoluteX = p1[0] + direction.x * along
          absoluteY = p1[1] + direction.y * along
        }
      }

      // 위치 변경이 없으면 갱신 생략 (스냅 상태에서의 불필요한 프레임당 연산 방지)
      const prevDot = tripodShapePoints.dots[index]
      if (prevDot && prevDot[0] === absoluteX && prevDot[1] === absoluteY) return

      // 절대좌표로 데이터 저장 (기존 방식과 동일)
      tripodShapePoints.dots[index] = [absoluteX, absoluteY]

      // 주축선 길이 조정 (시작점들의 범위에 맞춰)
      this.__adjustMainAxisLength(tripodShapePoints)

      // tripodShapePoints 업데이트 저장
      this.setTripodShapePoints(tripodShapePoints)

      // 주축선 실제 위치 업데이트
      this.__updateMainAxisLinePosition()

      // 시각적 요소들 전체 업데이트 (삼각형/원 포함)
      this.__updateTripodVisualElements()
    }

    /**
     * 주축선 드래그 핸들러 (EventManager 사용, 마우스 offset 계산 포함)
     */
    private __addMainAxisHandler(mainAxisLine: ILine): void {
      const interactionEnabled = this.getEntityInteractionMode()
      if (!interactionEnabled) {
        return
      }

      // 주축선 방향 판단
      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // const mainAxisStart = tripodShapePoints.mainAxisPoint1
      // const mainAxisEnd = tripodShapePoints.mainAxisPoint2
      // const _isVerticalMainAxis =
      //   Math.abs(mainAxisEnd[1] - mainAxisStart[1]) > Math.abs(mainAxisEnd[0] - mainAxisStart[0])

      // 드래그 시작 위치 저장용
      let dragStartMousePos = { x: 0, y: 0 }
      let dragStartMainAxisCenter = { x: 0, y: 0 }

      // Konva의 기본 draggable 사용
      mainAxisLine.draggable(interactionEnabled && (this as any).isSelected)

      // hover 이벤트 추가 (EventManager 사용)
      this.eventManager.removeEvent(mainAxisLine, CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_ENTER)
      this.eventManager.addEvent(mainAxisLine, CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_ENTER, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        this.renderer.stage.container().style.cursor = 'move'
        // 십자가 이동 커서
        this.node.getLayer()?.batchDraw()
      })

      this.eventManager.removeEvent(mainAxisLine, CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_LEAVE)
      this.eventManager.addEvent(mainAxisLine, CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_LEAVE, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        this.renderer.stage.container().style.cursor = 'default' // 기본 커서로 복원
        this.node.getLayer()?.batchDraw()
      })

      // 드래그 시작 - 마우스 시작 위치와 주축선 중심 저장
      this.eventManager.removeEvent(mainAxisLine, CANVAS_ENTITY_EVENT.SELECT_DRAG_START)
      this.eventManager.addEvent(mainAxisLine, CANVAS_ENTITY_EVENT.SELECT_DRAG_START, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        // 드래그 중 커서 유지
        this.renderer.stage.container().style.cursor = 'move'

        // 파동 애니메이션 제거 및 버튼그룹 숨김
        this.selectionHandler.removeShapeAnimation()
        this.selectionHandler.__hideButtonGroup()
        // TRIPOD 편집 버튼들 숨기기
        this.__hideTripodEditButtons()

        const stage = mainAxisLine.getStage()
        if (stage) {
          const pos = this.renderer.getPointerPosition()
          if (pos) {
            // 드래그 시작 시 마우스 위치 저장
            dragStartMousePos = { x: pos.x, y: pos.y }

            // 드래그 시작 시 주축선 중심 저장
            const currentTripodShapePoints = this.getTripodShapePoints()
            if (currentTripodShapePoints) {
              dragStartMainAxisCenter = {
                x:
                  (currentTripodShapePoints.mainAxisPoint1[0] +
                    currentTripodShapePoints.mainAxisPoint2[0]) /
                  2,
                y:
                  (currentTripodShapePoints.mainAxisPoint1[1] +
                    currentTripodShapePoints.mainAxisPoint2[1]) /
                  2,
              }
            }
          }
        }
      })

      // 드래그 중 - 마우스 이동량을 주축선 수직 성분으로만 적용
      this.eventManager.removeEvent(mainAxisLine, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE)
      this.eventManager.addEvent(mainAxisLine, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE, () => {
        const stage = mainAxisLine.getStage()
        if (!stage) return

        const pos = this.renderer.getPointerPosition()
        if (!pos) return

        // 마우스 이동량 계산
        const mouseDelta = {
          x: pos.x - dragStartMousePos.x,
          y: pos.y - dragStartMousePos.y,
        }

        // 현재 주축선 정보 가져오기
        const currentTripodShapePoints = this.getTripodShapePoints()
        if (!currentTripodShapePoints) return

        // 주축선에 수직인 방향 벡터
        const perpendicular = this.__getMainAxisPerpendicular(currentTripodShapePoints)

        // 마우스 이동량을 수직 방향에 투영
        const projectedDistance = mouseDelta.x * perpendicular.x + mouseDelta.y * perpendicular.y

        // 새로운 주축선 중심 위치 계산
        const newMainAxisCenter = {
          x: dragStartMainAxisCenter.x + perpendicular.x * projectedDistance,
          y: dragStartMainAxisCenter.y + perpendicular.y * projectedDistance,
        }

        // 주축선 수직 이동 적용
        this.__updateTripodMainAxisPosition(newMainAxisCenter)

        // Line 위치를 0으로 리셋 (데이터는 이미 업데이트됨)
        mainAxisLine.x(0)
        mainAxisLine.y(0)
      })

      // 드래그 종료
      this.eventManager.removeEvent(mainAxisLine, CANVAS_ENTITY_EVENT.SELECT_DRAG_END)
      this.eventManager.addEvent(mainAxisLine, CANVAS_ENTITY_EVENT.SELECT_DRAG_END, () => {
        // 최종 위치를 0으로 리셋
        mainAxisLine.x(0)
        mainAxisLine.y(0)

        // 커서 기본으로 복원
        this.renderer.stage.container().style.cursor = 'default'

        // 변형 완료 처리
        this.canvasEngine.events.onEntityTransform(this.getId())

        // Transformer 경계 영역 업데이트 (주축선 이동 반영)
        this.__updateTripodTransformer()

        // 파동 애니메이션 및 버튼그룹 복원
        if (this.balloon) {
          if (!this.getRippleFollowMode()) {
            this.selectionHandler.addShapeAnimation()
          } else {
            this.selectionHandler.updateRipplePosition()
          }
        }

        // TRIPOD 편집 버튼들 위치 업데이트 및 다시 표시
        this.__updateTripodEditButtonsPosition()
        this.__showTripodEditButtons()

        // 드래그 종료 후 이벤트 핸들러 재설정 제거 (__updateTripodVisualElements는 기존 요소 재사용하므로 핸들러 유지됨)
        // this.__addTripodElementHandlers()

        this.selectionHandler.__showButtonGroup()
        this.selectionHandler.__updateButtonPosition()
      })
    }

    /**
     * 주축선 중심을 새로운 위치로 이동 (startPoint 충돌 검사 포함)
     */
    private __updateTripodMainAxisPosition(newCenter: { x: number; y: number }): void {
      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // 현재 주축선의 방향과 길이 계산
      const currentVector = {
        x: tripodShapePoints.mainAxisPoint2[0] - tripodShapePoints.mainAxisPoint1[0],
        y: tripodShapePoints.mainAxisPoint2[1] - tripodShapePoints.mainAxisPoint1[1],
      }

      // 새로운 주축선 양끝점 계산
      const newMainAxisPoint1 = {
        x: newCenter.x - currentVector.x / 2,
        y: newCenter.y - currentVector.y / 2,
      }
      const newMainAxisPoint2 = {
        x: newCenter.x + currentVector.x / 2,
        y: newCenter.y + currentVector.y / 2,
      }

      // startPoint 충돌 검사
      const minSafeDistance = 15
      let isValidPosition = true

      for (const dot of tripodShapePoints.dots) {
        const startPoint = { x: dot[0], y: dot[1] }
        const distanceToNewCenter = Math.sqrt(
          Math.pow(startPoint.x - newCenter.x, 2) + Math.pow(startPoint.y - newCenter.y, 2),
        )

        if (distanceToNewCenter < minSafeDistance) {
          isValidPosition = false
          break
        }
      }

      // 유효한 위치인 경우에만 주축선 업데이트
      if (isValidPosition) {
        tripodShapePoints.mainAxisPoint1[0] = newMainAxisPoint1.x
        tripodShapePoints.mainAxisPoint1[1] = newMainAxisPoint1.y
        tripodShapePoints.mainAxisPoint2[0] = newMainAxisPoint2.x
        tripodShapePoints.mainAxisPoint2[1] = newMainAxisPoint2.y

        // 데이터 업데이트 및 시각적 요소 새로고침
        this.setTripodShapePoints(tripodShapePoints)

        // 주축선 시각적 요소 업데이트
        this.__updateMainAxisLinePosition()

        // 시각적 요소들 전체 업데이트 (연결선과 startPoint 시각 요소 포함)
        this.__updateTripodVisualElements()
        this.updateBalloonPosition()

        // 즉시 canvas에 반영
        this.node.getLayer()?.batchDraw()
      }
    }

    /**
     * TRIPOD 편집 버튼들 추가 (+ 버튼과 - 버튼들)
     */
    private __addTripodEditButtons(): void {
      if (this.entityType !== SHAPES.TRIPOD) return

      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // 기존 편집 버튼들 제거
      this.__removeTripodEditButtons()

      // 편집 버튼 그룹 생성
      this.__tripodEditButtonGroup = this.renderer.createGroup({
        name: 'tripod-edit-buttons',
      })

      // 주축선 양끝에 + 버튼 추가
      this.__addPlusButtons(tripodShapePoints)

      // 각 startpoint에 - 버튼 추가 (3개 이상일 때만)
      if (tripodShapePoints.dots.length >= 3) {
        this.__addMinusButtons(tripodShapePoints)
      }

      // 전체 이동 버튼 추가 (주축선 중심의 바깥쪽)
      this.__addMoveButton(tripodShapePoints)

      // TRIPOD 메인 그룹에 편집 버튼 그룹 추가
      if (this.__shape_wrapper && this.__tripodEditButtonGroup) {
        this.__shape_wrapper.add(this.__tripodEditButtonGroup)
        this.node.getLayer()?.batchDraw()
      }
    }
    /**
     * 주축선 양끝에 + 버튼들 추가 (주축선 방향 기준)
     */
    private __addPlusButtons(tripodShapePoints: TripodShapePoints): void {
      if (!this.__tripodEditButtonGroup) return

      const entityX = this.node.x()
      const entityY = this.node.y()
      const buttonOffset = 26 // 버튼과 주축선 끝점 사이의 거리

      // 주축선 방향 벡터 계산
      const direction = this.__getMainAxisDirection(tripodShapePoints)

      // 주축선 양끝점에서 방향 벡터를 따라 떨어진 위치에 버튼 배치
      const plusButton1AbsPos = {
        x: tripodShapePoints.mainAxisPoint1[0] - direction.x * buttonOffset,
        y: tripodShapePoints.mainAxisPoint1[1] - direction.y * buttonOffset,
      }
      const plusButton2AbsPos = {
        x: tripodShapePoints.mainAxisPoint2[0] + direction.x * buttonOffset,
        y: tripodShapePoints.mainAxisPoint2[1] + direction.y * buttonOffset,
      }

      // 절대좌표 → 상대좌표 변환
      const plusButton1Pos = {
        x: plusButton1AbsPos.x - entityX,
        y: plusButton1AbsPos.y - entityY,
      }
      const plusButton2Pos = {
        x: plusButton2AbsPos.x - entityX,
        y: plusButton2AbsPos.y - entityY,
      }

      // + 버튼들 생성 (방향을 구분하기 위해 start/end로 명명)
      const plusButton1 = this.__createPlusButton(plusButton1Pos, 'start')
      const plusButton2 = this.__createPlusButton(plusButton2Pos, 'end')

      // 편집 버튼 그룹에 추가
      this.__tripodEditButtonGroup.add(plusButton1)
      this.__tripodEditButtonGroup.add(plusButton2)

      // 저장
      this.__tripodPlusButtons.push(plusButton1, plusButton2)
    }

    /**
     * 각 startpoint에 - 버튼들 추가 (대각선 오른쪽 아래 배치)
     */
    private __addMinusButtons(tripodShapePoints: TripodShapePoints): void {
      if (!this.__tripodEditButtonGroup) return

      const entityX = this.node.x()
      const entityY = this.node.y()
      const buttonOffset = 26 // 버튼과 startPoint 사이의 거리

      tripodShapePoints.dots.forEach((dot, index) => {
        // 대각선 오른쪽 아래 (45도 방향)에 - 버튼 배치
        const diagonalOffsetX = buttonOffset * Math.cos(Math.PI / 4) // 45도 x 성분
        const diagonalOffsetY = buttonOffset * Math.sin(Math.PI / 4) // 45도 y 성분

        const minusButtonAbsPos = {
          x: dot[0] + diagonalOffsetX,
          y: dot[1] + diagonalOffsetY,
        }

        // 절대좌표 → 상대좌표 변환
        const minusButtonPos = {
          x: minusButtonAbsPos.x - entityX,
          y: minusButtonAbsPos.y - entityY,
        }

        const minusButton = this.__createMinusButton(minusButtonPos, index)

        // 편집 버튼 그룹에 추가
        if (this.__tripodEditButtonGroup) {
          this.__tripodEditButtonGroup.add(minusButton)
        }

        // 저장
        this.__tripodMinusButtons.push(minusButton)
      })
    }

    // 이동 버튼 위치 계산 — 주축선 중심에서 startPoint들의 반대(바깥) 방향 (절대좌표)
    private __getMoveButtonAbsPosition(tripodShapePoints: TripodShapePoints): {
      x: number
      y: number
    } {
      const center = {
        x: (tripodShapePoints.mainAxisPoint1[0] + tripodShapePoints.mainAxisPoint2[0]) / 2,
        y: (tripodShapePoints.mainAxisPoint1[1] + tripodShapePoints.mainAxisPoint2[1]) / 2,
      }
      const perp = this.__getMainAxisPerpendicular(tripodShapePoints)
      // startPoint들이 있는 쪽 판정 (수직 성분 합의 부호)
      let side = 0
      for (const dot of tripodShapePoints.dots) {
        side += (dot[0] - center.x) * perp.x + (dot[1] - center.y) * perp.y
      }
      const sign = side >= 0 ? -1 : 1
      const offset = 45
      return {
        x: center.x + perp.x * sign * offset,
        y: center.y + perp.y * sign * offset,
      }
    }

    /**
     * 전체 이동 버튼 추가 (주축선 중심의 바깥쪽, 드래그로 TRIPOD 전체 이동)
     */
    private __addMoveButton(tripodShapePoints: TripodShapePoints): void {
      if (!this.__tripodEditButtonGroup) return

      const absPos = this.__getMoveButtonAbsPosition(tripodShapePoints)
      const moveButton = this.__createMoveButton({
        x: absPos.x - this.node.x(),
        y: absPos.y - this.node.y(),
      })

      this.__tripodEditButtonGroup.add(moveButton)
      this.__tripodState.moveButton = moveButton
    }

    /**
     * 이동 버튼 생성 (십자 화살표 아이콘, 드래그 시 TRIPOD 전체 이동)
     */
    private __createMoveButton(pos: { x: number; y: number }): IGroup {
      const buttonGroup = this.renderer.createGroup({
        x: pos.x,
        y: pos.y,
        draggable: true,
      })

      // 배경 (원)
      const background = this.renderer.createCircle({
        x: 0,
        y: 0,
        radius: 18,
        fill: '#7d7d7d',
        stroke: '#fff',
        strokeWidth: 2,
      })

      // 터치 영역 확장을 위한 투명한 히트 영역
      const hitArea = this.renderer.createCircle({
        x: 0,
        y: 0,
        radius: 26,
        fill: 'transparent',
        listening: true,
      })

      // 십자 화살표 아이콘 (4방향 이동)
      const cross = this.renderer.createLine({
        points: [0, -10, 0, 10],
        stroke: '#fff',
        strokeWidth: 3,
        lineCap: 'round',
      })
      const crossH = this.renderer.createLine({
        points: [-10, 0, 10, 0],
        stroke: '#fff',
        strokeWidth: 3,
        lineCap: 'round',
      })
      const arrowHeads = [
        [-4, -6.5, 0, -11, 4, -6.5], // 상
        [-4, 6.5, 0, 11, 4, 6.5], // 하
        [-6.5, -4, -11, 0, -6.5, 4], // 좌
        [6.5, -4, 11, 0, 6.5, 4], // 우
      ].map(points =>
        this.renderer.createLine({
          points,
          stroke: '#fff',
          strokeWidth: 3,
          lineCap: 'round',
          lineJoin: 'round',
        }),
      )

      buttonGroup.add(hitArea, background, cross, crossH, ...arrowHeads)

      // 호버 이벤트
      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_ENTER)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_ENTER, () => {
        background.fill('#5a5a5a')
        this.node.getLayer()?.batchDraw()
        this.renderer.stage.container().style.cursor = 'move'
      })

      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_LEAVE)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_LEAVE, () => {
        background.fill('#7d7d7d')
        this.node.getLayer()?.batchDraw()
        this.renderer.stage.container().style.cursor = 'default'
      })

      // 드래그 기준 좌표 (드래그 델타 계산용 — entity와 데이터가 같은 델타로 이동하므로 불변)
      let baseX = 0
      let baseY = 0

      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.SELECT_DRAG_START)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.SELECT_DRAG_START, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return
        baseX = buttonGroup.x()
        baseY = buttonGroup.y()

        // 파동 애니메이션 제거 및 벌룬 버튼그룹 숨김 (이동 버튼 자신은 보이게 유지)
        this.selectionHandler.removeShapeAnimation()
        this.selectionHandler.__hideButtonGroup()
        ;[...this.__tripodPlusButtons, ...this.__tripodMinusButtons].forEach(button =>
          button.visible(false),
        )
      })

      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.SELECT_DRAG_MOVE, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return

        const dx = buttonGroup.x() - baseX
        const dy = buttonGroup.y() - baseY
        if (dx === 0 && dy === 0) return

        // 버튼 위치를 기준으로 리셋하고 Entity 전체를 이동 (connectionLine 드래그와 동일 패턴)
        buttonGroup.x(baseX)
        buttonGroup.y(baseY)
        this.setEntityPosition(this.node.x() + dx, this.node.y() + dy)
        this.__updateTripodPositionData(dx, dy)

        if (this.balloon && this.getRippleFollowMode()) {
          this.selectionHandler.updateRipplePosition()
        }
      })

      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.SELECT_DRAG_END)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.SELECT_DRAG_END, () => {
        if (!(this as any).isSelected || this.canvasEngine.isPinchZooming) return

        // 최종 위치 반영
        const dx = buttonGroup.x() - baseX
        const dy = buttonGroup.y() - baseY
        if (dx !== 0 || dy !== 0) {
          buttonGroup.x(baseX)
          buttonGroup.y(baseY)
          this.setEntityPosition(this.node.x() + dx, this.node.y() + dy)
          this.__updateTripodPositionData(dx, dy)
        }

        this.updateBalloonPosition()

        if (this.balloon) {
          if (!this.getRippleFollowMode()) {
            this.selectionHandler.addShapeAnimation()
          } else {
            this.selectionHandler.updateRipplePosition()
          }
        }

        // 변형 완료 이벤트 호출
        this.canvasEngine.events.onEntityTransform(this.getId())

        // 숨겼던 편집 버튼 복원 및 위치 갱신
        ;[...this.__tripodPlusButtons, ...this.__tripodMinusButtons].forEach(button =>
          button.visible(true),
        )
        this.__updateTripodEditButtonsPosition()

        this.selectionHandler.__showButtonGroup()
        this.selectionHandler.__updateButtonPosition()
      })

      return buttonGroup
    }

    /**
     * + 버튼 생성
     */
    private __createPlusButton(pos: { x: number; y: number }, position: 'start' | 'end'): IGroup {
      const buttonGroup = this.renderer.createGroup({
        x: pos.x,
        y: pos.y,
        draggable: false,
      })

      // + 버튼 배경 (원)
      const background = this.renderer.createCircle({
        x: 0,
        y: 0,
        radius: 16,
        fill: '#7d7d7d',
        stroke: '#fff',
        strokeWidth: 2,
      })

      // 터치 영역 확장을 위한 투명한 히트 영역 (태블릿 친화적)
      const hitArea = this.renderer.createCircle({
        x: 0,
        y: 0,
        radius: 24, // 더 큰 터치 영역
        fill: 'transparent',
        listening: true,
      })

      // + 아이콘 (세로선)
      const verticalLine = this.renderer.createLine({
        points: [0, -8, 0, 8],
        stroke: '#fff',
        strokeWidth: 3.5,
        lineCap: 'round',
      })

      // + 아이콘 (가로선)
      const horizontalLine = this.renderer.createLine({
        points: [-8, 0, 8, 0],
        stroke: '#fff',
        strokeWidth: 3.5,
        lineCap: 'round',
      })

      buttonGroup.add(hitArea, background, verticalLine, horizontalLine)

      // 마우스 및 터치 호버 이벤트 (EventManager 사용)
      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_ENTER)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_ENTER, () => {
        background.fill('#5a5a5a')
        this.node.getLayer()?.batchDraw()
        this.renderer.stage.container().style.cursor = 'pointer'
      })

      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_LEAVE)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_LEAVE, () => {
        background.fill('#7d7d7d')
        this.node.getLayer()?.batchDraw()
        this.renderer.stage.container().style.cursor = 'default'
      })

      // 클릭 및 터치 이벤트 (EventManager 사용)
      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_CLICK)
      this.eventManager.addEvent(
        buttonGroup,
        CANVAS_ENTITY_EVENT.EDIT_BUTTON_CLICK,
        (e: IPointerEvent) => {
          e.cancelBubble = true // 이벤트 전파 방지
          this.__onPlusButtonClick(position)
        },
      )

      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_TAP)
      this.eventManager.addEvent(
        buttonGroup,
        CANVAS_ENTITY_EVENT.EDIT_BUTTON_TAP,
        (e: IPointerEvent) => {
          e.cancelBubble = true // 이벤트 전파 방지
          this.__onPlusButtonClick(position)
        },
      )

      return buttonGroup
    }

    /**
     * - 버튼 생성
     */
    private __createMinusButton(pos: { x: number; y: number }, startPointIndex: number): IGroup {
      const buttonGroup = this.renderer.createGroup({
        x: pos.x,
        y: pos.y,
        draggable: false,
      })

      // - 버튼 배경 (원)
      const background = this.renderer.createCircle({
        x: 0,
        y: 0,
        radius: 16,
        fill: '#7d7d7d',
        stroke: '#fff',
        strokeWidth: 2,
      })

      // 터치 영역 확장을 위한 투명한 히트 영역 (태블릿 친화적)
      const hitArea = this.renderer.createCircle({
        x: 0,
        y: 0,
        radius: 24, // 더 큰 터치 영역
        fill: 'transparent',
        listening: true,
      })

      // - 아이콘 (가로선만)
      const horizontalLine = this.renderer.createLine({
        points: [-8, 0, 8, 0],
        stroke: '#fff',
        strokeWidth: 3.5,
        lineCap: 'round',
      })

      buttonGroup.add(hitArea, background, horizontalLine)

      // 마우스 및 터치 호버 이벤트 (EventManager 사용)
      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_ENTER)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_ENTER, () => {
        background.fill('#5a5a5a')
        this.node.getLayer()?.batchDraw()
        this.renderer.stage.container().style.cursor = 'pointer'
      })

      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_LEAVE)
      this.eventManager.addEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_MOUSE_LEAVE, () => {
        background.fill('#7d7d7d')
        this.node.getLayer()?.batchDraw()
        this.renderer.stage.container().style.cursor = 'default'
      })

      // 클릭 및 터치 이벤트 (EventManager 사용)
      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_CLICK)
      this.eventManager.addEvent(
        buttonGroup,
        CANVAS_ENTITY_EVENT.EDIT_BUTTON_CLICK,
        (e: IPointerEvent) => {
          e.cancelBubble = true // 이벤트 전파 방지
          this.__onMinusButtonClick(startPointIndex)
        },
      )

      this.eventManager.removeEvent(buttonGroup, CANVAS_ENTITY_EVENT.EDIT_BUTTON_TAP)
      this.eventManager.addEvent(
        buttonGroup,
        CANVAS_ENTITY_EVENT.EDIT_BUTTON_TAP,
        (e: IPointerEvent) => {
          e.cancelBubble = true // 이벤트 전파 방지
          this.__onMinusButtonClick(startPointIndex)
        },
      )

      return buttonGroup
    }

    /**
     * + 버튼 클릭 처리
     */
    private __onPlusButtonClick(position: 'start' | 'end'): void {
      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // 주축선 방향 계산
      const direction = this.__getMainAxisDirection(tripodShapePoints)

      // 클릭된 버튼에 따라 대상 주축선 끝점 결정
      const targetAxisPoint =
        position === 'start'
          ? { x: tripodShapePoints.mainAxisPoint1[0], y: tripodShapePoints.mainAxisPoint1[1] }
          : { x: tripodShapePoints.mainAxisPoint2[0], y: tripodShapePoints.mainAxisPoint2[1] }

      // 대상 끝점과 가장 가까운 startPoint 찾기
      let closestStartPoint: number[] | null = null
      let minDistance = Infinity

      for (const dot of tripodShapePoints.dots) {
        // startPoint를 주축선에 투영한 점 계산
        const projectedPoint = this.__projectPointOntoMainAxis(
          { x: dot[0], y: dot[1] },
          tripodShapePoints,
        )

        // 투영된 점과 대상 끝점 사이의 거리 계산
        const distance = Math.sqrt(
          Math.pow(projectedPoint.x - targetAxisPoint.x, 2) +
            Math.pow(projectedPoint.y - targetAxisPoint.y, 2),
        )

        if (distance < minDistance) {
          minDistance = distance
          closestStartPoint = dot
        }
      }

      if (!closestStartPoint) return

      // 가장 가까운 startPoint에서 주축선에 수직인 방향으로 30px 떨어진 새로운 점 생성
      // 기존 startPoint와 주축선 사이의 거리를 유지하면서 복사
      const startPointPos = { x: closestStartPoint[0], y: closestStartPoint[1] }
      const projectedStartPoint = this.__projectPointOntoMainAxis(startPointPos, tripodShapePoints)

      // 기존 startPoint에서 주축선까지의 벡터 (연결선 방향)
      const connectionVector = {
        x: startPointPos.x - projectedStartPoint.x,
        y: startPointPos.y - projectedStartPoint.y,
      }

      // 연결선 길이 (거리 유지용)
      const connectionDistance = Math.sqrt(
        connectionVector.x * connectionVector.x + connectionVector.y * connectionVector.y,
      )

      // 주축선을 따라 30px 이동한 지점에서 같은 거리만큼 수직으로 떨어진 새로운 startPoint 생성
      const offsetDistance = 30
      const offsetDirection = position === 'start' ? -1 : 1 // start는 반대방향, end는 순방향

      const newAxisPoint = {
        x: projectedStartPoint.x + direction.x * offsetDistance * offsetDirection,
        y: projectedStartPoint.y + direction.y * offsetDistance * offsetDirection,
      }

      // connectionVector를 정규화
      const connectionLength = Math.max(connectionDistance, 1) // 0으로 나누기 방지
      const normalizedConnection = {
        x: connectionVector.x / connectionLength,
        y: connectionVector.y / connectionLength,
      }

      // 새로운 startPoint 위치 = 새로운 축점 + (정규화된 연결벡터 * 원래 연결거리)
      const newStartPoint: number[] = [
        newAxisPoint.x + normalizedConnection.x * connectionDistance,
        newAxisPoint.y + normalizedConnection.y * connectionDistance,
      ]

      // dots 배열에 새로운 점 추가
      tripodShapePoints.dots.push(newStartPoint)

      // 주축선 길이 재조정
      this.__adjustMainAxisLength(tripodShapePoints)

      // TRIPOD 데이터 업데이트
      this.setTripodShapePoints(tripodShapePoints)

      // TRIPOD 구조 업데이트
      this.__updateTripodAfterEdit()

      // 편집 버튼들 다시 생성
      this.__addTripodEditButtons()

      // 변경사항 저장
      this.canvasEngine.events.onEntityTransform(this.getId())
    }

    /**
     * - 버튼 클릭 처리
     */
    private __onMinusButtonClick(startPointIndex: number): void {
      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // 최소 2개는 유지해야 함
      if (tripodShapePoints.dots.length < 3) {
        return
      }

      // 해당 인덱스의 startpoint 제거
      tripodShapePoints.dots.splice(startPointIndex, 1)

      // 주축선 길이 재조정 (수정된 로직으로 정확한 범위 계산)
      this.__adjustMainAxisLength(tripodShapePoints)

      // TRIPOD 데이터 먼저 업데이트 (balloon 이동 전에 데이터 동기화)
      this.setTripodShapePoints(tripodShapePoints)

      // balloon을 새로운 주축선의 중앙으로 이동
      this.__moveBalloonToMainAxisCenter(tripodShapePoints)

      // TRIPOD 구조 업데이트 (시각적 요소 재생성)
      this.__updateTripodAfterEdit()

      // 편집 버튼들 다시 생성
      this.__addTripodEditButtons()

      // 변경사항 저장
      this.canvasEngine.events.onEntityTransform(this.getId())
    }

    /**
     * balloon을 주축선의 중앙으로 이동 (startPoint 삭제 시 사용)
     */
    private __moveBalloonToMainAxisCenter(tripodShapePoints: TripodShapePoints): void {
      if (!this.balloon) return

      // 주축선의 중앙점 계산
      const mainAxisCenter = {
        x: (tripodShapePoints.mainAxisPoint1[0] + tripodShapePoints.mainAxisPoint2[0]) / 2,
        y: (tripodShapePoints.mainAxisPoint1[1] + tripodShapePoints.mainAxisPoint2[1]) / 2,
      }

      // 현재 balloon의 절대 위치
      const entityX = this.node.x()
      const entityY = this.node.y()
      const balloonPos = this.balloon.getPosition()
      const currentBalloonAbsPos = {
        x: entityX + balloonPos.x,
        y: entityY + balloonPos.y,
      }

      // 주축선 방향 벡터 계산
      const mainAxisDirection = {
        x: tripodShapePoints.mainAxisPoint2[0] - tripodShapePoints.mainAxisPoint1[0],
        y: tripodShapePoints.mainAxisPoint2[1] - tripodShapePoints.mainAxisPoint1[1],
      }
      const mainAxisLength = Math.sqrt(
        mainAxisDirection.x * mainAxisDirection.x + mainAxisDirection.y * mainAxisDirection.y,
      )
      const normalizedDirection = {
        x: mainAxisDirection.x / mainAxisLength,
        y: mainAxisDirection.y / mainAxisLength,
      }

      // 주축선에 수직인 방향 벡터
      const perpendicular = {
        x: -normalizedDirection.y,
        y: normalizedDirection.x,
      }

      // 현재 balloon의 주축선으로부터 수직 거리 계산
      const balloonToCenter = {
        x: currentBalloonAbsPos.x - mainAxisCenter.x,
        y: currentBalloonAbsPos.y - mainAxisCenter.y,
      }
      const perpendicularDistance =
        balloonToCenter.x * perpendicular.x + balloonToCenter.y * perpendicular.y

      // 새로운 balloon 위치: 주축선 중앙에서 기존 수직 거리 유지
      const newBalloonPosition = {
        x: mainAxisCenter.x + perpendicular.x * perpendicularDistance,
        y: mainAxisCenter.y + perpendicular.y * perpendicularDistance,
      }

      // balloon을 새 위치로 이동 (상대 좌표로 변환)
      this.balloon.setPosition({
        x: newBalloonPosition.x - entityX,
        y: newBalloonPosition.y - entityY,
      })

      // ripple animation 위치 업데이트
      this.selectionHandler.updateRipplePosition()
    }

    /**
     * TRIPOD 편집 후 구조 업데이트 (최적화된 배치 처리)
     */
    private __updateTripodAfterEdit(): void {
      // 캐시 무효화 (편집으로 요소들이 변경됨)
      this.__invalidateVisualElementsCache()

      // TRIPOD 시각적 요소들 완전히 다시 생성
      this.__recreateTripodVisualElements()

      // balloon text 동기화는 Balloon 모듈 내부에서 자동 처리됨

      // TRIPOD 편집 버튼들 다시 생성 (개수 변화 반영)
      this.__addTripodEditButtons()

      // 개별 요소 드래그 핸들러 다시 추가
      this.__addTripodElementHandlers()

      // 모든 업데이트를 배치로 처리 (중복 방지)
      this.__batchUpdateTripod({
        updateTransformer: true,
        updateButtons: true,
        updateBalloon: true,
        updateVisuals: false, // 이미 recreate했으므로 생략
        forceRender: true,
      })
    }

    /**
     * TRIPOD의 tailStyle을 변경하고 모든 startPoint를 업데이트합니다
     */
    public handleTripodTailShape(_tripodTailShapeId: string): void {
      if (!this.isTripod()) {
        throw new Error('이 메서드는 TRIPOD 도형에서만 사용할 수 있습니다.')
      }

      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) {
        throw new Error('TRIPOD shape points가 없습니다.')
      }

      // tailStyle 토글 (dot <-> arrow)
      const currentTailStyle = tripodShapePoints.tailStyle || 'dot'
      const newTailStyle = currentTailStyle === 'dot' ? 'arrow' : 'dot'

      // 개별 설정으로 저장
      this.setTailStyle(newTailStyle)

      // balloon 위치 업데이트
      this.updateBalloonPosition()

      // 데이터 변경 후 히스토리 백업 (setTailStyle 후에 호출)
      this.canvasEngine.events.onEntityTransform(this.getId())
    }

    /**
     * TRIPOD 시각적 요소들 위치/속성 업데이트 (이벤트 핸들러 유지)
     */
    private __updateTripodVisualElements(): void {
      if (!this.__tripod_transform_group) return

      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // tailStyle 체크 - 변경된 경우 재생성 필요
      const currentTailStyle = tripodShapePoints.tailStyle || 'dot'
      const hasArrowVisuals = this.__tripod_transform_group
        .getChildren()
        .some(node => isLineNode(node) && node.name() === 'startPointVisual')
      const shouldHaveArrows = currentTailStyle === 'arrow'

      // tailStyle이 변경된 경우 전체 재생성
      if (hasArrowVisuals !== shouldHaveArrows) {
        this.__recreateTripodVisualElements()
        return
      }

      const entityX = this.node.x()
      const entityY = this.node.y()

      // 주축선 업데이트 (기존 요소 재사용)
      let mainAxisLine: ILine | undefined
      this.__tripod_transform_group.getChildren().forEach(node => {
        if (isLineNode(node) && node.name() === 'mainAxis') {
          mainAxisLine = node
        }
      })

      if (mainAxisLine) {
        mainAxisLine.points([
          tripodShapePoints.mainAxisPoint1[0] - entityX,
          tripodShapePoints.mainAxisPoint1[1] - entityY,
          tripodShapePoints.mainAxisPoint2[0] - entityX,
          tripodShapePoints.mainAxisPoint2[1] - entityY,
        ])
      }

      // startPoint들과 연결선들 업데이트
      const startPointVisuals: IShape[] = []
      const startPointHits: IEllipse[] = []
      const connectionLines: ILine[] = []

      this.__tripod_transform_group.getChildren().forEach(node => {
        if (node.name() === 'startPointVisual') {
          if (isEllipseNode(node) || isLineNode(node)) {
            startPointVisuals.push(node)
          }
        } else if (isEllipseNode(node) && node.name() === 'startPoint') {
          startPointHits.push(node)
        } else if (isLineNode(node) && node.name() === 'connectionLine') {
          connectionLines.push(node)
        }
      })

      // startPoint 개수가 달라진 경우 또는 요소가 없는 경우를 대비한 안전한 업데이트
      if (
        startPointVisuals.length === tripodShapePoints.dots.length &&
        startPointHits.length === tripodShapePoints.dots.length &&
        connectionLines.length === tripodShapePoints.dots.length
      ) {
        // 기존 요소들이 모두 있으면 위치와 크기 업데이트 (이벤트 핸들러 유지)
        const currentPointRadius = this.calculatedPointSize
        const mainAxisHitWidth = 30
        const startPointHitWidth = Math.round(mainAxisHitWidth * 1.2)
        const hitRadius = Math.max(currentPointRadius + 6, startPointHitWidth / 2 + 2)
        const arrowMinLength = this.__getTripodArrowMinLength()

        tripodShapePoints.dots.forEach((dot, index) => {
          const visual = startPointVisuals[index]

          if (isEllipseNode(visual)) {
            // Dot 형태 (Ellipse) 업데이트
            visual.x(dot[0] - entityX)
            visual.y(dot[1] - entityY)
            visual.radiusX(currentPointRadius)
            visual.radiusY(currentPointRadius)
            visual.offsetY(currentPointRadius / 2)
          } else if (isLineNode(visual)) {
            // Arrow 형태 (Line) 업데이트 - 위치와 points 직접 업데이트
            const startPoint = { x: dot[0], y: dot[1] }
            const connectionEnd = this.__projectPointOntoMainAxis(startPoint, tripodShapePoints)
            // 최소길이 임계 안쪽이면 주축선 축의 바깥 방향을 가리킴
            const arrowDirection = computeTripodArrowDirection(
              dot,
              tripodShapePoints,
              connectionEnd,
              arrowMinLength,
            )

            // 삼각형 크기 설정 (방향도형/점도형 화살표와 동일한 크기)
            const sideLength = this.calculatedArrowHeadSize
            const height = (sideLength * Math.sqrt(3)) / 2
            const centroidToVertex = (2 * height) / 3
            const centroidToBase = height / 3

            const centerX = dot[0] - entityX
            const centerY = dot[1] - entityY

            // 정삼각형의 3개 꼭짓점 계산
            const tip = {
              x: centerX + arrowDirection.x * centroidToVertex,
              y: centerY + arrowDirection.y * centroidToVertex,
            }

            const perpendicular = { x: -arrowDirection.y, y: arrowDirection.x }
            const base1 = {
              x: centerX - arrowDirection.x * centroidToBase + perpendicular.x * (sideLength / 2),
              y: centerY - arrowDirection.y * centroidToBase + perpendicular.y * (sideLength / 2),
            }
            const base2 = {
              x: centerX - arrowDirection.x * centroidToBase - perpendicular.x * (sideLength / 2),
              y: centerY - arrowDirection.y * centroidToBase - perpendicular.y * (sideLength / 2),
            }

            // Line 위치와 points 업데이트
            visual.x(centerX)
            visual.y(centerY)
            visual.points([
              tip.x - centerX,
              tip.y - centerY,
              base1.x - centerX,
              base1.y - centerY,
              base2.x - centerX,
              base2.y - centerY,
              tip.x - centerX,
              tip.y - centerY,
            ])
          }

          // Hit circle 위치 및 크기 업데이트
          startPointHits[index].x(dot[0] - entityX)
          startPointHits[index].y(dot[1] - entityY)
          startPointHits[index].radiusX(hitRadius) // 개별 설정에 따른 hit 크기 적용
          startPointHits[index].radiusY(hitRadius) // 개별 설정에 따른 hit 크기 적용
          startPointHits[index].offsetY(hitRadius / 2) // 개별 설정에 따른 hit 크기 적용

          // ConnectionLine 업데이트
          const startPoint = { x: dot[0], y: dot[1] }
          const connectionEnd = this.__projectPointOntoMainAxis(startPoint, tripodShapePoints)

          connectionLines[index].points([
            startPoint.x - entityX,
            startPoint.y - entityY,
            connectionEnd.x - entityX,
            connectionEnd.y - entityY,
          ])
        })
      } else {
        // 요소 개수가 다르거나 없으면 recreate 방식 사용 (캐시도 무효화됨)
        this.__recreateTripodVisualElements()
        return
      }

      // 레이어 다시 그리기
      this.node.getLayer()?.batchDraw()
    }

    /**
     * TRIPOD 시각적 요소들을 완전히 다시 생성
     */
    private __recreateTripodVisualElements(): void {
      if (!this.isSelected) return
      if (!this.__tripod_transform_group) return

      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      // 캐시 무효화 (요소들이 재생성되므로)
      this.__invalidateVisualElementsCache()

      // 기존 시각적 요소들 모두 제거 (포괄적 제거로 잔상 방지)
      const elementsToRemove: INode[] = []
      this.__tripod_transform_group.getChildren().forEach(node => {
        if (
          (isEllipseNode(node) &&
            (node.name() === 'startPoint' || node.name() === 'startPointVisual')) ||
          (isLineNode(node) &&
            (node.name() === 'connectionLine' ||
              node.name() === 'mainAxis' ||
              node.name() === 'startPointVisual' ||
              !node.name()))
        ) {
          elementsToRemove.push(node)
        }
      })

      // 제거 (destroy로 완전 제거)
      elementsToRemove.forEach(node => {
        node.destroy()
      })

      // 새로운 요소들 생성
      const entityX = this.node.x()
      const entityY = this.node.y()
      const stroke = this.getStroke()
      const color = this.__color || (typeof stroke === 'string' ? stroke : '#000000')
      const pointRadius = this.calculatedPointSize
      const mainAxisHitWidth = 30
      const startPointHitWidth = Math.round(mainAxisHitWidth * 1.2)

      // 주축선 다시 생성
      const mainAxisLine = this.renderer.createLine({
        points: [
          tripodShapePoints.mainAxisPoint1[0] - entityX,
          tripodShapePoints.mainAxisPoint1[1] - entityY,
          tripodShapePoints.mainAxisPoint2[0] - entityX,
          tripodShapePoints.mainAxisPoint2[1] - entityY,
        ],
        stroke: color,
        strokeWidth: this.calculatedConnectedLineWidth,
        lineCap: 'round',
        name: 'mainAxis',
        listening: true,
        hitArea: { kind: 'strokeWidth', px: 30 },
        perfectDraw: false,
      })
      if (this.__tripod_transform_group) {
        this.__tripod_transform_group.add(mainAxisLine)

        // 주축선 생성 직후 바로 드래그 핸들러 추가 (타이밍 문제 방지)
        this.__addMainAxisHandler(mainAxisLine)
      }

      // 각 startpoint와 연결선들 다시 생성
      const currentTailStyle = tripodShapePoints.tailStyle || 'dot'
      const arrowMinLength = this.__getTripodArrowMinLength()

      tripodShapePoints.dots.forEach(dot => {
        const hitRadius = Math.max(pointRadius + 6, startPointHitWidth / 2 + 2)

        // tailStyle에 따라 다른 시각적 요소 생성
        let visualElement: IShape

        if (currentTailStyle === 'arrow') {
          // 화살표 방향 계산 (주축선 반대편, 최소길이 임계 안쪽이면 주축선 축의 바깥 방향)
          const startPoint = { x: dot[0], y: dot[1] }
          const connectionEnd = this.__projectPointOntoMainAxis(startPoint, tripodShapePoints)
          const arrowDirection = computeTripodArrowDirection(
            dot,
            tripodShapePoints,
            connectionEnd,
            arrowMinLength,
          )

          // 정삼각형 생성 (startPoint를 중심으로)
          const centerX = dot[0] - entityX
          const centerY = dot[1] - entityY

          // 정삼각형의 한 변의 길이 (방향도형/점도형 화살표와 동일한 크기)
          const sideLength = this.calculatedArrowHeadSize

          // 화살표 방향을 기준으로 정삼각형 생성
          // 삼각형의 무게중심을 startPoint 위치로 설정
          const height = (sideLength * Math.sqrt(3)) / 2 // 정삼각형의 높이
          const centroidToVertex = (2 * height) / 3 // 무게중심에서 꼭짓점까지의 거리
          const centroidToBase = height / 3 // 무게중심에서 밑변까지의 거리

          // 정삼각형의 3개 꼭짓점 계산
          const tip = {
            x: centerX + arrowDirection.x * centroidToVertex,
            y: centerY + arrowDirection.y * centroidToVertex,
          }

          // 밑변의 두 점 (화살표 방향에 수직인 방향으로 배치)
          const perpendicular = { x: -arrowDirection.y, y: arrowDirection.x }
          const base1 = {
            x: centerX - arrowDirection.x * centroidToBase + perpendicular.x * (sideLength / 2),
            y: centerY - arrowDirection.y * centroidToBase + perpendicular.y * (sideLength / 2),
          }
          const base2 = {
            x: centerX - arrowDirection.x * centroidToBase - perpendicular.x * (sideLength / 2),
            y: centerY - arrowDirection.y * centroidToBase - perpendicular.y * (sideLength / 2),
          }

          // 삼각형 Line 생성 (닫힌 형태, 좌표 시스템 수정)
          visualElement = this.renderer.createLine({
            x: centerX, // hitCircle과 동일한 x 위치
            y: centerY, // hitCircle과 동일한 y 위치
            points: [
              tip.x - centerX,
              tip.y - centerY, // 삼각형 끝점 (상대좌표)
              base1.x - centerX,
              base1.y - centerY, // 밑변 점1 (상대좌표)
              base2.x - centerX,
              base2.y - centerY, // 밑변 점2 (상대좌표)
              tip.x - centerX,
              tip.y - centerY, // 다시 끝점으로 (닫기)
            ],
            fill: color,
            stroke: color,
            strokeWidth: 1,
            closed: true,
            listening: false,
            name: 'startPointVisual',
            perfectDraw: false,
          })
        } else {
          // 기본 원형 (dot)
          visualElement = this.renderer.createEllipse({
            x: dot[0] - entityX,
            y: dot[1] - entityY,
            radiusX: pointRadius,
            radiusY: pointRadius,
            fill: color,
            stroke: color,
            strokeWidth: 1,
            listening: false,
            name: 'startPointVisual',
            perfectDraw: false,
          })
        }

        if (this.__tripod_transform_group) {
          this.__tripod_transform_group.add(visualElement)
        }

        // 연결선 생성
        this.__createConnectionLine(dot, tripodShapePoints, entityX, entityY, color)
        // Hit 영역 원
        const hitCircle = this.renderer.createEllipse({
          x: dot[0] - entityX,
          y: dot[1] - entityY,
          radiusX: hitRadius,
          radiusY: hitRadius,
          fill: color,
          stroke: color,
          strokeWidth: 1,
          listening: true,
          name: 'startPoint',
          opacity: 0,
          perfectDraw: false,
        })
        if (this.__tripod_transform_group) {
          this.__tripod_transform_group.add(hitCircle)
        }
      })

      // hover 상태 리셋 (시각적 요소 재생성 후)
      this.__resetTripodStartPointHover()

      // 재생성 후 즉시 이벤트 핸들러 추가
      this.__addTripodElementHandlers()

      // transformer 완전히 재생성
      this.__recreateTripodTransformer()

      // 레이어 다시 그리기
      this.node.getLayer()?.batchDraw()
    }

    /**
     * startpoint에서 주축선으로의 연결선 생성
     */
    private __createConnectionLine(
      dot: number[],
      tripodShapePoints: TripodShapePoints,
      entityX: number,
      entityY: number,
      color: string,
    ): void {
      const startPoint = { x: dot[0], y: dot[1] }

      // 시작점을 주축선에 수직으로 투영하여 연결점 찾기
      const connectionEnd = this.__projectPointOntoMainAxis(startPoint, tripodShapePoints)

      const connectionLine = this.renderer.createLine({
        points: [
          startPoint.x - entityX,
          startPoint.y - entityY,
          connectionEnd.x - entityX,
          connectionEnd.y - entityY,
        ],
        stroke: color,
        strokeWidth: this.calculatedConnectedLineWidth,
        lineCap: 'round',
        name: 'connectionLine',
        hitArea: { kind: 'strokeWidth', px: 20 }, // 넓은 hit 영역 (클릭 선택용 — 드래그 조작은 없음)
        listening: true, // 클릭 이벤트 수신
        perfectDraw: false,
      })

      if (this.__tripod_transform_group) {
        this.__tripod_transform_group.add(connectionLine)
      }
    }

    /**
     * TRIPOD transformer 경계 영역 업데이트
     * startpoint 추가/제거 시 transformer가 새로운 경계를 인식하도록 함
     */
    private __updateTripodTransformer(): void {
      if (this.__shapeTransformer && this.__tripod_transform_group) {
        // tripod_transform_group이 유효한지 확인
        try {
          if (
            !this.__tripod_transform_group.getParent() ||
            !this.__tripod_transform_group.getAttr('id')
          )
            return
        } catch {
          return
        }

        // nodes를 다시 설정하여 확실하게 경계 업데이트
        this.__shapeTransformer.attach([this.__tripod_transform_group])

        // transformer 강제 업데이트
        this.__shapeTransformer.forceUpdate()

        // system layer 다시 그리기
        const systemLayer = this.renderer.layers.system
        systemLayer?.batchDraw()
      }
    }

    /**
     * TRIPOD transformer를 완전히 제거하고 다시 생성
     */
    private __recreateTripodTransformer(): void {
      if (!this.__tripod_transform_group) return

      const systemLayer = this.renderer.layers.system
      if (!systemLayer) return

      // 기존 transformer 완전히 제거
      if (this.__shapeTransformer) {
        this.__shapeTransformer.destroy()
        this.__shapeTransformer = undefined
      }

      const interactionEnabled = this.getEntityInteractionMode()
      const transformTarget = this.__tripod_transform_group

      // transformer 설정 (회전만 가능, 크기 변경 불가능)
      this.__shapeTransformer = this.createSelectionHandles(
        {
          id: 'single-selection',
          padding: 20,
          anchorCornerRadius: 20,
          anchorSize: 20, // 다른 shape들과 동일한 앵커 크기
          rotateEnabled: interactionEnabled, // 회전만 활성화
          resizeEnabled: false, // 크기 변경 비활성화
          enabledAnchors: [], // 리사이즈 앵커는 모두 비활성화 (회전 핸들만 표시)
          borderStrokeWidth: 2,
          ignoreStroke: true,
          borderHitStrokeWidth: TRIPOD_CONSTANTS.TRANSFORMER_HIT_STROKE_WIDTH,
          draggable: interactionEnabled,
          anchorDraggable: false, // 앵커 드래그 비활성화 (회전 핸들은 동작)
        },
        [transformTarget],
      )

      // TRIPOD Transformer 이벤트 핸들러 추가
      this.__addTripodTransformerHandlers()

      this.canvasEngine.initShapeTransformer(this.__shapeTransformer)
      systemLayer.add(this.__shapeTransformer.node)
      systemLayer.moveToTop()
    }

    /**
     * TRIPOD 편집 버튼들 숨기기
     */
    private __hideTripodEditButtons(): void {
      if (this.__tripodEditButtonGroup) {
        this.__tripodEditButtonGroup.visible(false)
        this.node.getLayer()?.batchDraw()
      }
    }

    /**
     * TRIPOD 편집 버튼들 표시
     */
    private __showTripodEditButtons(): void {
      if (this.__tripodEditButtonGroup) {
        this.__tripodEditButtonGroup.visible(true)
        this.node.getLayer()?.batchDraw()
      }
    }

    /**
     * TRIPOD 편집 버튼들의 위치 업데이트 (주축선 기준)
     */
    private __updateTripodEditButtonsPosition(): void {
      if (this.entityType !== SHAPES.TRIPOD) return
      if (!this.__tripodEditButtonGroup) return

      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      const entityX = this.node.x()
      const entityY = this.node.y()
      const buttonOffset = 26 // 버튼과 주축선 끝점 사이의 거리

      // + 버튼들 위치 업데이트
      if (this.__tripodPlusButtons.length >= 2) {
        // 주축선 방향 벡터 계산
        const direction = this.__getMainAxisDirection(tripodShapePoints)

        // 주축선 양끝점에서 방향 벡터를 따라 15px 떨어진 위치에 버튼 배치
        const plusButton1AbsPos = {
          x: tripodShapePoints.mainAxisPoint1[0] - direction.x * buttonOffset,
          y: tripodShapePoints.mainAxisPoint1[1] - direction.y * buttonOffset,
        }
        const plusButton2AbsPos = {
          x: tripodShapePoints.mainAxisPoint2[0] + direction.x * buttonOffset,
          y: tripodShapePoints.mainAxisPoint2[1] + direction.y * buttonOffset,
        }

        // 절대좌표 → 상대좌표 변환 후 적용
        const plusButton1Pos = {
          x: plusButton1AbsPos.x - entityX,
          y: plusButton1AbsPos.y - entityY,
        }
        const plusButton2Pos = {
          x: plusButton2AbsPos.x - entityX,
          y: plusButton2AbsPos.y - entityY,
        }

        this.__tripodPlusButtons[0].x(plusButton1Pos.x)
        this.__tripodPlusButtons[0].y(plusButton1Pos.y)
        this.__tripodPlusButtons[1].x(plusButton2Pos.x)
        this.__tripodPlusButtons[1].y(plusButton2Pos.y)
      }

      // - 버튼들 위치 업데이트 (대각선 오른쪽 아래 배치)
      this.__tripodMinusButtons.forEach((minusButton, index) => {
        if (index < tripodShapePoints.dots.length) {
          const dot = tripodShapePoints.dots[index]

          // 대각선 오른쪽 아래 (45도 방향)에 - 버튼 배치
          const diagonalOffsetX = buttonOffset * Math.cos(Math.PI / 4) // 45도 x 성분
          const diagonalOffsetY = buttonOffset * Math.sin(Math.PI / 4) // 45도 y 성분

          const minusButtonPos = {
            x: dot[0] + diagonalOffsetX - entityX,
            y: dot[1] + diagonalOffsetY - entityY,
          }

          minusButton.x(minusButtonPos.x)
          minusButton.y(minusButtonPos.y)
        }
      })

      // 이동 버튼 위치 업데이트
      if (this.__tripodState.moveButton) {
        const moveAbsPos = this.__getMoveButtonAbsPosition(tripodShapePoints)
        this.__tripodState.moveButton.x(moveAbsPos.x - entityX)
        this.__tripodState.moveButton.y(moveAbsPos.y - entityY)
      }

      // 레이어 다시 그리기
      this.node.getLayer()?.batchDraw()
    }

    /**
     * TRIPOD 편집 버튼들 제거
     */
    private __removeTripodEditButtons(): void {
      // 편집 버튼들의 이벤트 먼저 정리
      ;[...this.__tripodPlusButtons, ...this.__tripodMinusButtons].forEach(button => {
        this.__removeNodeEvents(button, 'editButton')
      })
      // 이동 버튼은 호버(editButton) + 드래그(startPoint와 동일 구성) 이벤트를 모두 정리
      if (this.__tripodState.moveButton) {
        this.__removeNodeEvents(this.__tripodState.moveButton, 'editButton')
        this.__removeNodeEvents(this.__tripodState.moveButton, 'startPoint')
      }

      // 편집 버튼 그룹 제거
      if (this.__tripodEditButtonGroup) {
        this.__tripodEditButtonGroup.remove()
        this.__tripodEditButtonGroup = undefined
      }

      // 배열 비우기
      this.__tripodState.plusButtons.length = 0
      this.__tripodState.minusButtons.length = 0
      this.__tripodState.moveButton = null

      // 레이어 다시 그리기
      this.node.getLayer()?.batchDraw()
    }

    /**
     * 주축선 Line 노드의 실제 위치 업데이트
     */
    private __updateMainAxisLinePosition(): void {
      if (!this.__tripod_transform_group || !this.__shape) return

      const tripodShapePoints = this.getTripodShapePoints()
      if (!tripodShapePoints) return

      const tripodTransformGroup = this.__tripod_transform_group
      const entityX = this.node.x()
      const entityY = this.node.y()

      // 주축선 찾기
      tripodTransformGroup.getChildren((node: INode) => {
        if (isLineNode(node) && node.name() === 'mainAxis') {
          const mainAxisStart = tripodShapePoints.mainAxisPoint1
          const mainAxisEnd = tripodShapePoints.mainAxisPoint2

          // 주축선 points 업데이트 (entity 상대 좌표로)
          const newPoints = [
            mainAxisStart[0] - entityX,
            mainAxisStart[1] - entityY,
            mainAxisEnd[0] - entityX,
            mainAxisEnd[1] - entityY,
          ]

          node.points(newPoints)
        }
        return false
      })
    }

    /**
     * 점을 주축선에 수직으로 투영하여 연결점 찾기
     */
    private __projectPointOntoMainAxis(
      point: { x: number; y: number },
      tripodShapePoints: TripodShapePoints,
    ): { x: number; y: number } {
      const mainAxisStart = {
        x: tripodShapePoints.mainAxisPoint1[0],
        y: tripodShapePoints.mainAxisPoint1[1],
      }
      const mainAxisEnd = {
        x: tripodShapePoints.mainAxisPoint2[0],
        y: tripodShapePoints.mainAxisPoint2[1],
      }

      // 주축선 벡터
      const mainAxisVector = {
        x: mainAxisEnd.x - mainAxisStart.x,
        y: mainAxisEnd.y - mainAxisStart.y,
      }

      // 시작점에서 주축선 시작점까지의 벡터
      const pointToAxisStart = {
        x: point.x - mainAxisStart.x,
        y: point.y - mainAxisStart.y,
      }

      // 주축선 벡터의 길이 제곱
      const mainAxisLengthSquared =
        mainAxisVector.x * mainAxisVector.x + mainAxisVector.y * mainAxisVector.y

      if (mainAxisLengthSquared === 0) {
        // 주축선 길이가 0인 경우 시작점 반환
        return mainAxisStart
      }

      // 투영 계수 계산 (0~1 범위로 클램핑)
      const projectionRatio = Math.max(
        0,
        Math.min(
          1,
          (pointToAxisStart.x * mainAxisVector.x + pointToAxisStart.y * mainAxisVector.y) /
            mainAxisLengthSquared,
        ),
      )

      // 투영점 계산
      return {
        x: mainAxisStart.x + mainAxisVector.x * projectionRatio,
        y: mainAxisStart.y + mainAxisVector.y * projectionRatio,
      }
    }

    /**
     * 시작점들 범위에 맞춰 주축선 길이 조정 (주축선 방향 기준)
     */
    private __adjustMainAxisLength(tripodShapePoints: TripodShapePoints): void {
      // startpoint가 없으면 조정하지 않음
      if (tripodShapePoints.dots.length === 0) {
        return
      }

      // 현재 주축선의 중심점과 방향
      const mainAxisCenter = {
        x: (tripodShapePoints.mainAxisPoint1[0] + tripodShapePoints.mainAxisPoint2[0]) / 2,
        y: (tripodShapePoints.mainAxisPoint1[1] + tripodShapePoints.mainAxisPoint2[1]) / 2,
      }
      const direction = this.__getMainAxisDirection(tripodShapePoints)

      // 각 시작점을 주축선에 투영하여 거리 계산
      const projectedDistances: number[] = []

      for (const dot of tripodShapePoints.dots) {
        // 시작점에서 주축선 중심까지의 벡터
        const vectorToCenter = {
          x: dot[0] - mainAxisCenter.x,
          y: dot[1] - mainAxisCenter.y,
        }

        // 주축선 방향으로 투영한 거리 (부호 있음)
        const projectedDistance = vectorToCenter.x * direction.x + vectorToCenter.y * direction.y
        projectedDistances.push(projectedDistance)
      }

      // 실제 최소값과 최대값 찾기 (0으로 초기화하지 않음)
      const minProjectedDistance = Math.min(...projectedDistances)
      const maxProjectedDistance = Math.max(...projectedDistances)

      // 주축선을 시작점들의 투영 범위에 정확히 맞춤 (불필요한 연장 방지)
      // 새로운 주축선 양끝점 계산
      tripodShapePoints.mainAxisPoint1[0] = mainAxisCenter.x + direction.x * minProjectedDistance
      tripodShapePoints.mainAxisPoint1[1] = mainAxisCenter.y + direction.y * minProjectedDistance
      tripodShapePoints.mainAxisPoint2[0] = mainAxisCenter.x + direction.x * maxProjectedDistance
      tripodShapePoints.mainAxisPoint2[1] = mainAxisCenter.y + direction.y * maxProjectedDistance
    }
    /**
     * 주축선의 방향 벡터 계산
     */
    private __getMainAxisDirection(tripodShapePoints: TripodShapePoints): { x: number; y: number } {
      const p1 = tripodShapePoints.mainAxisPoint1
      const p2 = tripodShapePoints.mainAxisPoint2
      const dx = p2[0] - p1[0]
      const dy = p2[1] - p1[1]
      const length = Math.sqrt(dx * dx + dy * dy)

      // 정규화된 방향 벡터 반환
      return length > 0 ? { x: dx / length, y: dy / length } : { x: 1, y: 0 }
    }

    /**
     * 주축선에 수직인 벡터 계산
     */
    private __getMainAxisPerpendicular(tripodShapePoints: TripodShapePoints): {
      x: number
      y: number
    } {
      const direction = this.__getMainAxisDirection(tripodShapePoints)
      // 수직 벡터는 (x, y) -> (-y, x) 또는 (y, -x)
      return { x: -direction.y, y: direction.x }
    }

    // 화살표헤드-선 최소길이 임계 (tailStyle이 'arrow'가 아니면 0, 줌레벨 보정은 direction 도형과 동일 감각)
    private __getTripodArrowMinLength(): number {
      const tripodShapePoints = this.getTripodShapePoints()
      if ((tripodShapePoints?.tailStyle || 'dot') !== 'arrow') return 0
      const scale = this.renderer.stage.scaleX() || 1
      return TRIPOD_ARROW_MIN_LENGTH * (1 + (TRIPOD_ARROW_MIN_LENGTH_FACTOR - 1) * (1 / scale - 1))
    }

    /**
     * TRIPOD 관련 stage 이벤트 정리
     */
    private __cleanupTripodStageEvents(): void {
      const stage = this.renderer.stage
      if (!stage) return

      // 기존 직접 이벤트 리스너들 정리 (혹시 남아있을 수 있는)
      const eventTypes = [
        'mousemove.tripod-axis',
        'mouseup.tripod-axis',
        'touchmove.tripod-axis',
        'touchend.tripod-axis',
      ]

      eventTypes.forEach(eventType => {
        stage.off(eventType)
      })

      // 시작점 관련 이벤트들 정리
      const tripodShapePoints = this.selectionHandler.getTripodShapePoints()
      if (tripodShapePoints) {
        for (let i = 0; i < tripodShapePoints.dots.length; i++) {
          stage.off(`mousemove.tripod-start-${i}`)
          stage.off(`mouseup.tripod-start-${i}`)
          stage.off(`touchmove.tripod-start-${i}`)
          stage.off(`touchend.tripod-start-${i}`)
        }
      }
    }

    /**
     * TRIPOD 전용 ActionButtonGroup 위치 계산 (상/하로만 배치, rotation handle 고려)
     */
    private __calculateTripodButtonPosition(): { x: number; y: number } {
      const transformerBounds = this.__getTransformerBounds()
      const stage = this.renderer.stage
      const scale = stage.scaleX()

      // TRIPOD의 실제 기하학적 중심점 계산 (회전 고려)
      const actualCenter = this.__calculateTripodActualCenter()

      // 스케일에 독립적인 고정 DOM 픽셀 값들 (최종 DOM에서 일정한 거리 유지)
      const FIXED_TRIPOD_OFFSET_PX = 80 // DOM 픽셀 단위 고정값
      const FIXED_ROTATION_HANDLE_OFFSET_PX = 65 // DOM 픽셀 단위 고정값
      const FIXED_VERTICAL_THRESHOLD_PX = 25 // DOM 픽셀 단위 고정값

      // Canvas 좌표계로 변환 (scale로 나누어 Canvas 상에서의 거리 계산)
      const tripodOffset = FIXED_TRIPOD_OFFSET_PX / scale
      const rotationHandleOffset = FIXED_ROTATION_HANDLE_OFFSET_PX / scale
      const verticalThreshold = FIXED_VERTICAL_THRESHOLD_PX / scale

      // balloon이 있는 경우 balloon 위치에 따라 상/하 결정
      if (this.selectionHandler.balloon) {
        const entityY = this.node.y()
        const balloonPos = this.selectionHandler.balloon.getPosition()
        const balloonAbsY = entityY + balloonPos.y

        // balloon과 실제 중심점과의 수직 위치 비교 (Canvas 좌표계)
        const deltaY = balloonAbsY - actualCenter.y

        if (deltaY > verticalThreshold) {
          // balloon이 아래쪽 → 버튼을 위쪽에 배치 (rotation handle 고려)
          return {
            x: actualCenter.x,
            y: transformerBounds.top - tripodOffset - rotationHandleOffset,
          }
        } else if (deltaY < -verticalThreshold) {
          // balloon이 위쪽 → 버튼을 아래쪽에 배치
          return {
            x: actualCenter.x,
            y: transformerBounds.bottom + tripodOffset,
          }
        } else {
          // balloon이 가운데 영역 → 아래쪽에 배치 (기본값)
          return {
            x: actualCenter.x,
            y: transformerBounds.bottom + tripodOffset,
          }
        }
      }

      // 기본값: transformer 아래쪽에 배치 (balloon이 없는 경우)
      return {
        x: actualCenter.x,
        y: transformerBounds.bottom + tripodOffset,
      }
    }

    /**
     * TRIPOD의 실제 기하학적 중심점을 계산하는 함수 (회전 고려)
     */
    private __calculateTripodActualCenter(): { x: number; y: number } {
      const tripodShapePoints = this.selectionHandler.getTripodShapePoints()
      if (!tripodShapePoints) {
        // fallback: entity 위치
        return { x: this.node.x(), y: this.node.y() }
      }

      // 주축선의 중심점 계산
      const mainAxisCenterX =
        (tripodShapePoints.mainAxisPoint1[0] + tripodShapePoints.mainAxisPoint2[0]) / 2
      const mainAxisCenterY =
        (tripodShapePoints.mainAxisPoint1[1] + tripodShapePoints.mainAxisPoint2[1]) / 2

      // 모든 startPoint들의 중심점 계산
      let startPointsCenterX = 0
      let startPointsCenterY = 0
      if (tripodShapePoints.dots.length > 0) {
        tripodShapePoints.dots.forEach(dot => {
          startPointsCenterX += dot[0]
          startPointsCenterY += dot[1]
        })
        startPointsCenterX /= tripodShapePoints.dots.length
        startPointsCenterY /= tripodShapePoints.dots.length
      }

      // 주축선 중심점과 startPoint들 중심점의 평균을 구하여 실제 기하학적 중심점 계산
      const actualCenterX = (mainAxisCenterX + startPointsCenterX) / 2
      const actualCenterY = (mainAxisCenterY + startPointsCenterY) / 2

      return { x: actualCenterX, y: actualCenterY }
    }

    /**
     * transformer의 경계 영역을 계산하는 함수 (TRIPOD용)
     */
    private __getTransformerBounds(): {
      top: number
      bottom: number
      left: number
      right: number
      centerX: number
      centerY: number
    } {
      const stage = this.renderer.stage
      const scale = stage.scaleX()
      const entityX = this.node.x()
      const entityY = this.node.y()

      // TRIPOD의 경우 모든 요소(mainAxis, startPoints, balloon)를 고려한 경계 계산
      const tripodShapePoints = this.selectionHandler.getTripodShapePoints()
      if (tripodShapePoints) {
        let minX = Number.MAX_VALUE
        let minY = Number.MAX_VALUE
        let maxX = Number.MIN_VALUE
        let maxY = Number.MIN_VALUE

        // 주축선 끝점들 포함
        const mainAxisPoints = [
          tripodShapePoints.mainAxisPoint1[0],
          tripodShapePoints.mainAxisPoint1[1],
          tripodShapePoints.mainAxisPoint2[0],
          tripodShapePoints.mainAxisPoint2[1],
        ]

        // 모든 startPoint들 포함
        const allPoints = [...mainAxisPoints]
        tripodShapePoints.dots.forEach(dot => {
          allPoints.push(dot[0], dot[1])
        })

        // balloon 위치 포함
        if (this.selectionHandler.balloon) {
          const balloonPos = this.selectionHandler.balloon.getPosition()
          const balloonX = entityX + balloonPos.x
          const balloonY = entityY + balloonPos.y
          const balloonSize = this.selectionHandler.balloon.head.rect.width() || 60
          allPoints.push(balloonX - balloonSize / 2, balloonY - balloonSize / 2)
          allPoints.push(balloonX + balloonSize / 2, balloonY + balloonSize / 2)
        }

        // 경계 계산
        for (let i = 0; i < allPoints.length; i += 2) {
          const x = allPoints[i]
          const y = allPoints[i + 1]
          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }

        // 스케일에 독립적인 고정 DOM 픽셀 값들 (transformer bounds 계산용)
        const FIXED_TRIPOD_PADDING_PX = 80 // DOM 픽셀 단위 고정값
        const FIXED_ANCHOR_SIZE_PX = 40 // DOM 픽셀 단위 고정값

        // Canvas 좌표계로 변환 (scale로 나누어 Canvas 상에서의 거리 계산)
        const tripodPadding = FIXED_TRIPOD_PADDING_PX / scale
        const anchorSize = FIXED_ANCHOR_SIZE_PX / scale

        // 실제 기하학적 중심점 사용
        const actualCenter = this.__calculateTripodActualCenter()

        return {
          left: minX - tripodPadding,
          right: maxX + tripodPadding,
          top: minY - tripodPadding - anchorSize,
          bottom: maxY + tripodPadding,
          centerX: actualCenter.x, // 실제 중심점 사용
          centerY: actualCenter.y, // 실제 중심점 사용
        }
      }

      // 기본값: entity 자체의 위치
      return {
        left: entityX - 20,
        right: entityX + 20,
        top: entityY - 20,
        bottom: entityY + 20,
        centerX: entityX,
        centerY: entityY,
      }
    }

    /**
     * TRIPOD 색상 업데이트
     * @description mixin 형태로 selection.ts에서 동적 호출됨
     */

    private __updateTripodColors(color: string): void {
      const tripodTransformGroup = this.selectionHandler.__tripod_transform_group
      if (!tripodTransformGroup) return

      // 연결선들 색상 업데이트
      const connectionLines = tripodTransformGroup.find(
        (node: INode) => isLineNode(node) && node.name() === 'connectionLine',
      ) as ILine[]
      connectionLines.forEach(line => line.stroke(color))

      // startpoint들 색상 업데이트 (hit 영역)
      const startPoints = tripodTransformGroup.find(
        (node: INode) => isEllipseNode(node) && node.name() === 'startPoint',
      ) as IEllipse[]
      startPoints.forEach(circle => {
        circle.stroke(color)
        circle.fill(color)
      })

      // startpoint들 색상 업데이트 (시각적 원)
      const visualPoints = tripodTransformGroup.find(
        (node: INode) => isEllipseNode(node) && node.name() === 'startPointVisual',
      ) as IEllipse[]
      visualPoints.forEach(circle => {
        circle.stroke(color)
        circle.fill(color)
      })
    }
  }
}
